import {
  AgXToneMapping,
  Color,
  Group,
  HalfFloatType,
  Mesh,
  PerspectiveCamera,
  Raycaster,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three';
// Postprocessing is dynamic-imported (issue #130): type-only here so it pulls
// no runtime code into the entry chunk; the classes are loaded lazily in
// initPostprocessing() below.
import type { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import type { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { CameraRig, type NavMode } from './camera/cameraRig';
import { EYE_HEIGHT_M } from './camera/groundLock';
import { ViewpointCoordinateMissingError, viewpointMenuEntry } from './camera/viewpointJump';
import { poseForMyLocation, type FootprintRing } from './camera/myLocation';
import { geolocationAvailable, readCompassHeading, readCurrentPosition } from './camera/myLocationControl';
import {
  TRUE_SCALE_FOV_DEG,
  fovForPreset,
  nextViewScalePreset,
  officialSheetFraming,
  presetAfterExternalFov,
  viewScalePresetLabel,
  type ViewScalePreset,
} from './camera/viewScale';
import { AngularScaleBar } from './ui/angularScaleBar';
import { formatMethodologyCaption } from './ui/methodologyCaption';
import { formatAssessmentBasis } from './ui/assessmentBasis';
import {
  BootIntroSweep,
  DEFAULT_END_OFFSET_M,
  DEFAULT_END_Y_AOD,
  introEndPose,
  resolveBootIntroAnchor,
  shouldPlayBootIntro,
  type IntroSessionStorage,
} from './camera/bootIntro';
import { RoutePlayer, centroidTarget, nearestPointOnRoute, type RouteTarget } from './camera/routePlayer';
import { LOCAL_ORIGIN_EASTING, LOCAL_ORIGIN_NORTHING, bngToLocal, localToBng } from './core/coordinates';
import {
  loadBuildings,
  loadAppearanceGroundTruth,
  loadLocations,
  loadFacilities,
  loadWater,
  loadRoads,
  loadRoadsDraped,
  loadLandcover,
  loadManifest,
  loadTurbineScenarios,
  loadViewpoints,
  tileLocalBounds,
  type BuildingsFile,
  type AppearanceGroundTruth,
  type LocationsFile,
  type FacilitiesFile,
  type WaterFile,
  type RoadsFile,
  type RoadsDrapedFile,
  type LandcoverFile,
  type TurbineScenariosFile,
  type TurbineScenario,
  type Viewpoint,
  type ViewpointsFile,
} from './core/manifest';
import { loadRoute, type RoutePolyline } from './core/route';
import {
  detectQualityTier,
  qualitySettings,
  parseQualityOverride,
  resolveQualityTier,
  loadSavedQualityTier,
  saveQualityTier,
  QUALITY_STORAGE_KEY,
  type QualityTier,
  type QualityStorage,
} from './core/quality';
import { applyQualitySettings, type QualityApplyTarget } from './core/applyQuality';
import {
  QUALITY_CHOICES,
  choiceLabel,
  choiceTier,
  initialChoice,
  planQualitySwitch,
  type QualityChoice,
} from './ui/qualityControl';
import {
  loadSavedShadingPreset,
  parseShadingOverride,
  resolveShadingPreset,
  saveShadingPreset,
  type ShadingPreset,
} from './core/shadingPreset';
import { SHADING_CHOICES, shadingLabel } from './ui/shadingControl';
import {
  loadDrawerOpen,
  saveDrawerOpen,
  loadLayerVisibility,
  saveLayerVisibility,
  resolveLayerVisibility,
  type LayerId,
} from './ui/hudPrefs';
import {
  DEFAULT_GRADE,
  GRADE_FRAGMENT_SHADER,
  GRADE_VERTEX_SHADER,
  gradeUniforms,
  parseGradeOverride,
  parsePostOverride,
} from './scene/colorGrade';
import { setupLighting } from './scene/lighting';
import { ShadowRig, type AoiBounds } from './scene/shadowRig';
import { applyDistanceFade, FOG_REFERENCE_DISTANCE_M, updateDistanceFadeDensity } from './scene/fade';
import { ATMOSPHERE_PALETTE, rgbToHex } from './scene/atmosphere';
import { SkyDome } from './scene/sky';
import { BuildingsLayer, footprintToLocal } from './scene/buildings';
import { LandmarksLayer } from './scene/landmarksLayer';
import { BeaconsLayer } from './scene/beacons';
import { BearingLinesLayer } from './scene/bearings';
import { WaterLayer } from './scene/water';
import { RoadWallsLayer } from './scene/roadWalls';
import { VegetationLayer } from './scene/vegetation';
import { RocksLayer } from './scene/rocks';
import { TarmacClearance } from './scene/tarmacClearance';
import { TreeMeshPools } from './scene/trees/treePools';
import { loadTrees, type BakedTrees } from './scene/trees/treeAssets';
import { buildTurbines, turbinePlacements, splitTurbineScenarios, RotorAnimator } from './scene/turbines';
import { RouteRibbon } from './terrain/routeRibbon';
import { RoadRibbonsLayer } from './terrain/roadRibbons';
import { TileLoader } from './terrain/tileLoader';
import { computeReadout, formatReadout } from './ui/coordinateReadout';
import { isBlockerStatus } from './ui/statusText';
import { createStatusSequencer } from './ui/statusSequencer';
import { turbineEntities, viewpointEntities } from './ui/minimap';
import { toMarkerPoints, toViewpointMarkerPoints } from './ui/minimapMarkers';
import type { OsmMinimap } from './ui/osmMinimap';
import { LazyMinimap } from './ui/lazyMinimap';
import { loadLazy } from './core/lazy';
import { createPerfOverlay } from './ui/perfOverlay';
import { ControlsOverlay } from './ui/controlsOverlay';
import { loadKeyboardLayoutMap } from './ui/keyboardLayout';
import { createCompactSignal, createTouchOnlySignal, requiresForcedOrbitSwitch } from './ui/layoutMode';
import { defaultNavMode, navModeButtonsHtml, resolveEnabledNavModes } from './ui/navModes';
import { MobileSheet } from './ui/mobileSheet';
import { RouteStrip } from './ui/routeStrip';

// Turbines sit up to ~8 km from the viewer, so terrain must render well past
// them or they appear to hover above the near-terrain's cut-off edge. The
// whole tiled AOI is only ~180 small tiles, so we load a wide radius (the
// horizon), keeping a small initial load for a fast first paint.
const BOOT_LOAD_RADIUS_M = 3000;
const LOAD_RADIUS_M = 10000;
const UNLOAD_RADIUS_M = 12000;
const TILE_UPDATE_INTERVAL_S = 1.5;
/** Max collider raycasts each scenery layer (walls, water) may spend per tile
 * tick re-draping onto newly-streamed terrain. Each raycast is ~0.5-1 ms of
 * main-thread time against the LOD1 TIN, so an unbounded pass froze the frame
 * loop for seconds (the walls/water 2 s animate/freeze cycle). Layers cache
 * every resolved height and report when they need another call, so the work
 * finishes across a few ticks instead of stalling one. */
const GROUND_SAMPLES_PER_TICK = 150;
const ROUTE_GPX_URL = '/data/config/woodburn.gpx';

/**
 * Best-effort unmasked GPU renderer string (issue #17), used to auto-detect
 * integrated GPUs. Uses a throwaway context so it can run before the main
 * renderer is constructed (antialias/powerPreference are fixed at that point).
 * Returns undefined where the extension is unavailable (e.g. privacy modes).
 */
function probeGpuRenderer(): string | undefined {
  try {
    const gl = document.createElement('canvas').getContext('webgl2') ?? document.createElement('canvas').getContext('webgl');
    if (!gl) return undefined;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const value = ext ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string) : gl.getParameter(gl.RENDERER);
    return typeof value === 'string' ? value : undefined;
  } catch {
    return undefined;
  }
}

async function boot(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) throw new Error('missing #app root element');
  // Enabled nav-modes config (issue #143): decides which mode buttons the
  // toolbar renders and which mode the viewer boots in. Default = Explore +
  // Drive; `?modes=all` (or an explicit list) re-enables the retained
  // Orbit/Fly/Walk. An empty/all-invalid override degrades to the default set
  // (hard constraint #7 — never a modeless toolbar). Resolved before
  // buildLayout so the template renders exactly the enabled buttons.
  const enabledNavModes = resolveEnabledNavModes(window.location.search);
  const bootNavMode = defaultNavMode(enabledNavModes);
  app.innerHTML = buildLayout(enabledNavModes);

  const canvas = document.getElementById('scene-canvas') as HTMLCanvasElement;
  const hudEl = document.getElementById('hud')!;
  const readoutEl = document.getElementById('readout')!;
  const modeButtons = document.querySelectorAll<HTMLButtonElement>('[data-mode]');
  const viewpointSelect = document.getElementById('viewpoint-select') as HTMLSelectElement;
  const myLocationBtn = document.getElementById('my-location-btn') as HTMLButtonElement;
  const statusEl = document.getElementById('status')!;
  const viewScaleToggle = document.getElementById('view-scale-toggle') as HTMLButtonElement;
  // Methodology caption (issue #178) and assessment-basis readout (issue
  // #179): two disclosure panels sharing this HUD surface, in the slot the
  // True-scale-only viewing-distance hint used to occupy. Unlike that hint,
  // both are always visible — their wording itself carries the caveat for
  // whichever preset/scenario is active, never a hide/show toggle.
  //
  // Each is a single element that WRAPS, never a CSS ellipsis (post-review
  // fix): `formatMethodologyCaption`/`formatAssessmentBasis` put the
  // preset's claim / envelope / honesty caveat FIRST (`guaranteed`) and keep
  // it short by construction, so it always renders in full — a single
  // truncated string with that wording at the END was the original defect,
  // letting a CSS ellipsis silently cut it on a phone. See
  // ui/methodologyCaption.ts and ui/assessmentBasis.ts for the full
  // reasoning, including how the free-text scenario source (genuinely
  // unbounded) is truncated instead, with `title` carrying it in full.
  const methodologyCaptionEl = document.getElementById('methodology-caption') as HTMLElement;
  const assessmentBasisEl = document.getElementById('assessment-basis') as HTMLElement;

  // Compact (phone) layout + bottom sheet (issue #109, made hamburger-only by
  // #132): a re-parenting bottom sheet holding everything except the minimap
  // and the hamburger itself. Constructed here, before any async load below,
  // so a BLOCKER at boot (e.g. a manifest load failure) still renders
  // through the sheet's banner pull-out on a phone — hard constraint #7's
  // escape hatch must be visible on first paint, not just once the rest of
  // boot() has run. `items` snapshots `#layers-drawer`'s current children
  // (its checkboxes, blade toggle, turbine select and legend) rather than
  // hardcoding each by id, so a future drawer content change is picked up
  // automatically; the drawer element itself stays behind (hidden by CSS
  // while compact — see style.css) since only its *contents* move into the
  // sheet. `#caveat` moves as a whole unit now (issue #132 — its collapsed
  // one-line summary no longer stays visible outside the sheet, unlike
  // #109's original design), and `#mode-control`/`#viewpoint-select` join it
  // for the same reason: nothing but the hamburger and the minimap is
  // persistent chrome while compact any more.
  const modeControlEl = document.getElementById('mode-control') as HTMLElement;
  const mobileSheetEl = document.getElementById('mobile-sheet') as HTMLElement;
  const mobileSheetContentEl = document.getElementById('mobile-sheet-content') as HTMLElement;
  const mobileSheetToggleEl = document.getElementById('mobile-sheet-toggle') as HTMLButtonElement;
  const mobileSheetCloseEl = document.getElementById('mobile-sheet-close') as HTMLButtonElement;
  const qualityControlEl = document.getElementById('quality-control') as HTMLElement;
  const shadingControlEl = document.getElementById('shading-control') as HTMLElement;
  const layersDrawerContentsEl = document.getElementById('layers-drawer') as HTMLElement;
  const caveatEl = document.getElementById('caveat') as HTMLElement;
  const mapSheetSectionEl = document.getElementById('map-sheet-section') as HTMLElement;
  const hudStripEl = document.getElementById('hud-strip') as HTMLElement;
  const hudBottomEl = document.getElementById('hud-bottom') as HTMLElement;
  const mobileSheet = new MobileSheet({
    sheetEl: mobileSheetEl,
    contentEl: mobileSheetContentEl,
    toggleEl: mobileSheetToggleEl,
    closeEl: mobileSheetCloseEl,
    sceneEl: canvas,
    // #my-location-btn is deliberately absent here (issue #168 follow-up):
    // it lives inline in #top-bar (see buildLayout) and is lifted to a
    // fixed top-right position only under touch (see style.css), so it
    // stays reachable whether or not the sheet is open and never overlaps
    // the other top-bar controls on desktop.
    items: [
      modeControlEl,
      viewpointSelect,
      qualityControlEl,
      shadingControlEl,
      viewScaleToggle,
      methodologyCaptionEl,
      assessmentBasisEl,
      ...(Array.from(layersDrawerContentsEl.children) as HTMLElement[]),
      mapSheetSectionEl,
      caveatEl,
    ],
    hudStripEl,
    bannerParentEl: hudBottomEl,
  });

  // Compact layout signal (issue #109): observed live via matchMedia's
  // change event (never boot-time-only), same pattern as the touch-only
  // signal further below — a resize or rotation across the breakpoint flips
  // the layout mid-session with no reload. A missing or throwing
  // `window.matchMedia` falls back to `false` (the full desktop layout).
  const compactSignal = createCompactSignal(
    typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : undefined,
  );
  function renderCompact(compact: boolean): void {
    hudEl.dataset.compact = String(compact);
    mobileSheet.setCompact(compact);
  }
  renderCompact(compactSignal.matches);
  compactSignal.subscribe(renderCompact);

  // A BLOCKER-prefixed message (hard constraint #7's escape hatch) gets a
  // distinct style and expands to its own full-width line within the merged
  // bottom strip (issue #76) so it can never be truncated or mistaken for
  // routine status chatter — see the `#status.status--blocker` rule in
  // style.css. While compact, the same signal also drives `mobileSheet`'s
  // banner pull-out (issue #109): a missing-input report must never be
  // buried in a closed sheet.
  //
  // statusSequencer (issue #84) guards against a stale, unawaited write (the
  // background "fill to horizon" tile load's `.then()`) clobbering a newer,
  // more specific message: every setStatus call commits a generation token,
  // and setStatusIfCurrent only applies a deferred write if no newer commit
  // has happened since the token it captured. This needs no BLOCKER special
  // case — a BLOCKER call to setStatus is itself just a newer commit, so it
  // always wins over an older, still-pending token.
  const statusSequencer = createStatusSequencer();
  function setStatus(text: string): number {
    statusEl.textContent = text;
    const blocker = isBlockerStatus(text);
    statusEl.classList.toggle('status--blocker', blocker);
    mobileSheet.setBlocker(blocker);
    return statusSequencer.commit();
  }
  function setStatusIfCurrent(token: number, text: string): void {
    if (statusSequencer.isCurrent(token)) setStatus(text);
  }

  // Adaptive quality tier (issue #17 / #72 / #73): resolve the tier BEFORE
  // creating the renderer, with the full precedence URL `?quality=` > saved
  // choice > auto-detect (resolveQualityTier). Reading the persisted choice here
  // — before the WebGL context exists — is the reload-loop guard: the context is
  // created with the right MSAA from the start, so a switch that crossed the
  // antialias boundary and reloaded boots straight into the correct antialias
  // and never needs to reload again.
  const gpuRenderer = probeGpuRenderer();
  const autoEnv = {
    hardwareConcurrency: navigator.hardwareConcurrency,
    devicePixelRatio: window.devicePixelRatio,
    gpuRenderer,
  };
  // Real localStorage, wrapped so a throwing store (private browsing) is treated
  // as "no saved choice" by the helpers rather than crashing boot.
  const qualityStorage: QualityStorage = {
    get: (key) => window.localStorage.getItem(key),
    set: (key, value) => window.localStorage.setItem(key, value),
  };

  // Layers drawer (issue #74): open/closed state and per-layer visibility
  // persist in localStorage via the same injected-storage pattern as the
  // quality control above — qualityStorage's get/set shape already matches
  // HudPrefsStorage, so it is reused rather than wrapping window.localStorage
  // a second time.
  const layersToggle = document.getElementById('layers-toggle') as HTMLButtonElement;
  const layersDrawer = document.getElementById('layers-drawer') as HTMLElement;
  let drawerOpen = loadDrawerOpen(qualityStorage);
  function renderDrawer(): void {
    layersDrawer.classList.toggle('hidden', !drawerOpen);
    layersToggle.setAttribute('aria-expanded', String(drawerOpen));
    layersToggle.classList.toggle('active', drawerOpen);
  }
  renderDrawer();
  layersToggle.addEventListener('click', () => {
    drawerOpen = !drawerOpen;
    saveDrawerOpen(qualityStorage, drawerOpen);
    renderDrawer();
  });

  // Controls overlay (issue #87): per-mode keyboard/mouse legend, top-right,
  // dismissible via the ⌨ toggle. Same injected-storage reuse as the drawer
  // above. Touch-only devices hide it entirely via CSS (`pointer: coarse`
  // and `hover: none`, style.css) — no JS feature-detect needed for that
  // case. The keyboard layout map is best-effort and resolves after first
  // paint; setMode() below renders the QWERTY fallback immediately so the
  // legend is never blocked on it.
  const controlsOverlayEl = document.getElementById('controls-overlay') as HTMLElement;
  const controlsToggleEl = document.getElementById('controls-toggle') as HTMLButtonElement;
  const controlsOverlay = new ControlsOverlay(controlsOverlayEl, controlsToggleEl, qualityStorage);
  void loadKeyboardLayoutMap(window.navigator).then((layoutMap) => controlsOverlay.setLayoutMap(layoutMap));

  // Collapsible legal caveat (issue #76): one-line summary by default,
  // expanding to the full wording (verbatim, hard constraint #5) including
  // the OpenStreetMap ODbL attribution. No persistence — this one always
  // starts collapsed, unlike the Layers drawer above.
  const caveatToggle = document.getElementById('caveat-toggle') as HTMLButtonElement;
  const caveatBody = document.getElementById('caveat-body') as HTMLElement;
  let caveatOpen = false;
  function renderCaveat(): void {
    caveatBody.classList.toggle('hidden', !caveatOpen);
    caveatToggle.setAttribute('aria-expanded', String(caveatOpen));
  }
  renderCaveat();
  caveatToggle.addEventListener('click', () => {
    caveatOpen = !caveatOpen;
    renderCaveat();
  });

  const layerVisibility = loadLayerVisibility(qualityStorage);
  function persistLayerVisibility(id: LayerId, visible: boolean): void {
    layerVisibility[id] = visible;
    saveLayerVisibility(qualityStorage, layerVisibility);
  }
  /** Disables a layer's drawer checkbox with a tooltip reason (hard
   * constraint #7: a missing input is a reported blocker, never fabricated
   * data) — the disabled row wins regardless of any persisted choice. */
  function disableLayerToggle(toggle: HTMLInputElement | null, reason: string): void {
    if (!toggle) return;
    toggle.disabled = true;
    toggle.title = reason;
    const row = toggle.closest('.layer-row');
    if (row instanceof HTMLElement) {
      row.title = reason;
      row.classList.add('layer-row--disabled');
    }
  }

  const removeSavedChoice = (): boolean => {
    try {
      window.localStorage.removeItem(QUALITY_STORAGE_KEY);
      return true;
    } catch {
      return false;
    }
  };
  const urlOverride = parseQualityOverride(window.location.search);
  const savedChoice = loadSavedQualityTier(qualityStorage);
  // What "Auto" resolves to (auto-detect only), shown on the control's Auto label.
  const autoDetectedTier = detectQualityTier(autoEnv);
  let tier: QualityTier = resolveQualityTier({
    search: window.location.search,
    storage: qualityStorage,
    env: autoEnv,
  });
  let quality = qualitySettings(tier);
  // Backdrop blur has a real GPU cost (issue #74): gate the card system's
  // `backdrop-filter` off the resolved tier via this attribute rather than a
  // per-frame check — see the `#hud[data-quality-tier="low"]` rule in
  // style.css.
  hudEl.dataset.qualityTier = tier;

  // Terrain shading preset (issue #106): stylised (flat-shaded, today's look)
  // vs natural (smooth normals + a continuous low-tier ramp). Orthogonal to
  // the quality tier above — resolved the same URL > saved > default way, but
  // with no auto-detection axis and no antialias-style reload boundary, so
  // (unlike quality) it is safe to resolve here, after renderer construction,
  // and every switch applies live via TileLoader.setShadingPreset.
  const shadingUrlOverride = parseShadingOverride(window.location.search);
  const shadingSavedChoice = loadSavedShadingPreset(qualityStorage);
  let shadingPreset: ShadingPreset = resolveShadingPreset({
    search: window.location.search,
    storage: qualityStorage,
  });
  hudEl.dataset.shadingPreset = shadingPreset;
  // The antialias bucket's MAXIMUM settings. InstancedMesh buffers + candidate
  // vegetation density are sized to this so a live med↔high switch (both within
  // the antialias-on bucket) has headroom to re-cap without reallocating or
  // regenerating. A low (antialias-off) session is its own bucket and can only
  // leave it via a reload, so it stays sized to LOW.
  const allocQuality = quality.antialias ? qualitySettings('high') : qualitySettings('low');

  const renderer = new WebGLRenderer({
    canvas,
    antialias: quality.antialias,
    // Ask hybrid-graphics laptops for the discrete GPU rather than the weakest
    // integrated one (issue #17).
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.pixelRatioCap));
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = AgXToneMapping;
  // MSAA is fixed for this page load. A quality switch that would change it
  // reloads (persisting the choice first); everything else applies live. Kept
  // separate from `quality` because a storage-unavailable fallback applies a
  // target tier's non-antialias settings live while this real value stays put.
  const contextAntialias = quality.antialias;
  // Read-only handle for headless perf measurement (mirrors __ray2_scene).
  (window as unknown as { __ray2_renderer: WebGLRenderer }).__ray2_renderer = renderer;

  // Opt-in perf capture (issue #17, "decouple capture from analysis"):
  // undefined unless ?perf=1, so the default path never builds a recorder,
  // a BootTimer, or overlay DOM (see perfOverlay.ts for the zero-cost
  // gating contract). Cosmetic/diagnostic only — never read by LOS code.
  const perfOverlay = createPerfOverlay(window.location.search, {
    getDeviceInfo: () => ({
      gpuRenderer,
      devicePixelRatio: window.devicePixelRatio,
      hardwareConcurrency: navigator.hardwareConcurrency,
      qualityTier: tier,
      userAgent: navigator.userAgent,
    }),
  });

  const scene = new Scene();
  // Sky gradient dome + exp-squared distance fog (issue #60): both read
  // their colours from scene/atmosphere.ts's single palette (also used for
  // the sun/ambient light tint in setupLighting below), so sky, fog, and
  // sunlight can never drift out of sync. scene.background stays set as a
  // solid fallback clear colour behind the dome — fade.ts's pre-#60 history
  // found that nulling it clears to black, which reads as perpetual night.
  const skyDome = new SkyDome();
  scene.add(skyDome.mesh);
  scene.background = new Color(rgbToHex(ATMOSPHERE_PALETTE.horizon));
  applyDistanceFade(scene);
  const lighting = setupLighting(scene, renderer, { shadows: quality.shadows, shadowMapSize: quality.shadowMapSize });

  // Boots at the True scale preset (27° vertical, issue #95): a 60° vertical
  // FOV reads everything at roughly ×0.47 of the apparent size a desktop
  // monitor actually subtends at normal viewing distance, so both turbine
  // scale and hill steepness were systematically undersold. 27° matches the
  // Landscape Institute TGN 06/19 50mm full-frame-equivalent convention.
  const camera = new PerspectiveCamera(TRUE_SCALE_FOV_DEG, 1, 0.5, 40000);
  // Default boot pose: local origin (E 390978 N 586358), a little above
  // ground so the first frame renders before any tile/collider is ready.
  camera.position.set(0, 250, 0);

  // View-scale toggle (issue #95, third preset added by #177): True scale ->
  // Explore -> Official sheet, a one-shot projection-matrix update — never
  // per-frame work, and presentation-only (FOV never feeds any LOS result,
  // hard constraint #1). Defined here, right after the camera exists, so both
  // the HUD button/hint and every jump path (which may change camera.fov) can
  // call renderViewScale() to stay in sync.
  //
  // The active preset is tracked explicitly rather than re-derived from
  // camera.fov (issue #177): Official sheet's *vertical* FOV depends on the
  // live viewport aspect, so a FOV alone cannot identify it — and at some
  // aspects it coincides exactly with 27° or 60°. Anything that sets
  // camera.fov behind this control's back (a viewpoint's explicit fov_deg)
  // goes through presetAfterExternalFov in renderViewScale below.
  let viewScalePreset: ViewScalePreset = 'true-scale';
  // Eye height disclosed by both the methodology caption (issue #178) and the
  // assessment-basis readout (issue #179) — one shared piece of state so the
  // two panels never disagree with each other. Starts at the documented
  // default, labelled as such: no saved viewpoint has been jumped to yet at
  // boot, so there is no viewpoint-authored figure to show instead (hard
  // constraint #7 — never present a default as though it were a stated
  // value). `setActiveEyeHeight` below (defined once `activeScenario` also
  // exists) is what a viewpoint jump / "my location" fix update this through.
  let activeEyeHeightM: number = EYE_HEIGHT_M;
  let activeEyeHeightIsDefault = true;
  function renderMethodologyCaption(): void {
    const cap = formatMethodologyCaption({
      preset: viewScalePreset,
      fovDeg: camera.fov,
      eyeHeightM: activeEyeHeightM,
      eyeHeightIsDefault: activeEyeHeightIsDefault,
    });
    // `cap.text` wraps (style.css: normal white-space, no ellipsis) rather
    // than clipping — see the module comment for why this matters.
    methodologyCaptionEl.textContent = cap.text;
    // `title` keeps the same wording reachable on hover/focus too.
    methodologyCaptionEl.title = cap.text;
  }
  const angularScaleBar = new AngularScaleBar(
    document.getElementById('angular-scale-bar') as HTMLElement,
  );
  /** Viewport width the scale bar spans, refreshed by `resize()` below. */
  let scaleBarWidthPx = window.innerWidth;
  /** Horizontal FOV the scale bar is graduated against — cached here rather
   * than re-derived on the render loop (repo perf rule: no per-frame
   * allocation). Only ever changes when the preset or the viewport shape
   * does, both of which land in renderViewScale below. */
  let officialSheetHfovDeg = officialSheetFraming(camera.aspect).horizontalFovDeg;
  function renderViewScale(): void {
    viewScalePreset = presetAfterExternalFov(viewScalePreset, camera.fov, camera.aspect);
    viewScaleToggle.textContent = viewScalePresetLabel(viewScalePreset, camera.fov, camera.aspect);
    renderMethodologyCaption();
    officialSheetHfovDeg = officialSheetFraming(camera.aspect).horizontalFovDeg;
    // The bar itself is driven from the render loop (it needs the live
    // bearing); leaving the preset alone here means a toggle away from
    // Official sheet hides it on the very next frame.
  }
  function applyViewScalePreset(preset: ViewScalePreset): void {
    // Presentation only: camera.fov and the overlay, never position, aim, nav
    // mode, or any in-progress animation (boot intro, route playback,
    // viewpoint jump all keep running untouched).
    viewScalePreset = preset;
    if (preset !== 'custom') camera.fov = fovForPreset(preset, camera.aspect);
    camera.updateProjectionMatrix();
    renderViewScale();
  }
  function toggleViewScale(): void {
    applyViewScalePreset(nextViewScalePreset(viewScalePreset));
  }
  renderViewScale();
  viewScaleToggle.addEventListener('click', toggleViewScale);
  // `V` mirrors the button in every camera mode, including mid route
  // playback — it only ever touches camera.fov, never position/mode/aim.
  // Ignored while a form control has focus (e.g. the viewpoint <select>) so
  // it can't fire while the user is typing/selecting there.
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyV') return;
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    toggleViewScale();
  });

  // Colour grade post pass (issue #59): AgX tone mapping above plus a
  // teal-orange split-tone grade, applied as a single full-screen ShaderPass
  // wrapped in an EffectComposer, extended by #64's bloom. `?grade=off`
  // disables the ShaderPass for A/B comparison; the chain always ends in an
  // OutputPass, which applies the renderer's tone mapping and sRGB output
  // transform once, so the disabled render matches what
  // `renderer.render(scene, camera)` would produce.
  //
  // Lazy-loaded (issue #130): EffectComposer + the bloom/grade/output passes
  // are ~cosmetic and pull a separate chunk that the first terrain paint does
  // not need. Until that chunk resolves — and permanently if it fails to load
  // (offline / captive portal / partial redeploy, hard constraint #7) — the
  // frame loop renders straight through `renderer.render()`, which still
  // applies AgX tone mapping and native MSAA; only bloom and the split-tone
  // grade are absent, and nothing here ever feeds a LOS result (hard
  // constraint #1).
  //
  // Bloom (issue #64): threshold sits well above mid-grey scene luminance
  // (~0.5) so only the sky dome / sun-lit highlights bleed. Perf (issue #17):
  // the resolution passed in (and re-applied in `resize()`, since
  // EffectComposer.setSize would otherwise reset every pass to full composer
  // resolution) is half the canvas in each dimension. Disabled outright on the
  // low tier via `quality.bloom`, and by `?post=off` for A/B screenshots.
  const BLOOM_RESOLUTION_SCALE = 0.5;
  const BLOOM_STRENGTH = 0.3;
  const BLOOM_RADIUS = 0.4;
  const BLOOM_THRESHOLD = 0.85;
  const postEnabled = parsePostOverride(window.location.search);
  let composer: EffectComposer | undefined;
  let bloomPass: UnrealBloomPass | undefined;

  async function initPostprocessing(): Promise<void> {
    const res = await loadLazy(() => import('./scene/postprocessing'));
    if (!res.ok) {
      // Degraded-but-booted (hard constraint #7): keep rendering via
      // renderer.render() (AgX + MSAA, no bloom/grade). Cosmetic only.
      console.warn('postprocessing unavailable (rendering without bloom/grade)', res.error);
      return;
    }
    const { EffectComposer, RenderPass, ShaderPass, UnrealBloomPass, OutputPass } = res.value;
    // AA note (issue #17): the composer's offscreen render target has no MSAA
    // by default, which would silently drop the antialias the renderer was
    // constructed with. `samples` restores it on WebGL2 when `quality.antialias`
    // is on, 0 on the low tier — matching the fixed-at-construction contract.
    const pr = renderer.getPixelRatio();
    const size = renderer.getSize(new Vector2());
    const renderTarget = new WebGLRenderTarget(size.width * pr, size.height * pr, {
      type: HalfFloatType,
      samples: quality.antialias ? 4 : 0,
    });
    const c = new EffectComposer(renderer, renderTarget);
    c.addPass(new RenderPass(scene, camera));
    const bp = new UnrealBloomPass(
      new Vector2(
        Math.round(size.width * pr * BLOOM_RESOLUTION_SCALE),
        Math.round(size.height * pr * BLOOM_RESOLUTION_SCALE),
      ),
      BLOOM_STRENGTH,
      BLOOM_RADIUS,
      BLOOM_THRESHOLD,
    );
    bp.enabled = quality.bloom && postEnabled;
    c.addPass(bp);
    const gp = new ShaderPass({
      uniforms: gradeUniforms(DEFAULT_GRADE, postEnabled),
      vertexShader: GRADE_VERTEX_SHADER,
      fragmentShader: GRADE_FRAGMENT_SHADER,
    });
    gp.enabled = parseGradeOverride(window.location.search);
    c.addPass(gp);
    // OutputPass applies AgX tone mapping + sRGB exactly once (scene materials
    // skip it when rendering into the linear offscreen target), whichever
    // earlier passes are enabled.
    c.addPass(new OutputPass());
    composer = c;
    bloomPass = bp;
    // Size the freshly-built composer/bloom to the live canvas (resize also
    // re-applies the half-res bloom budget); the next frame renders through it.
    resize();
  }
  // initPostprocessing() is invoked at the end of boot (just before the frame
  // loop), NOT here: its resize() call touches state (e.g. minimapGate) declared
  // further down, so building the composer during an early boot await would hit
  // a temporal-dead-zone error. The chunk is tiny (~19 kB) and the frame loop
  // renders via renderer.render() until it resolves, so an early kickoff buys
  // nothing.

  const rig = new CameraRig(camera, canvas);
  // Boot in the configured default mode (issue #143): the rig constructs in
  // Explore (the community default); if the enabled-modes config puts a
  // different mode first, switch to it now so the viewer never boots into a
  // mode whose button isn't rendered. setMode is a no-op when already there
  // (the common Explore case), so this only does work for a non-default config.
  if (rig.mode !== bootNavMode) rig.setMode(bootNavMode);
  // Initial legend content (issue #87): seed the overlay with the boot mode's
  // row set before the first frame rather than waiting for the first
  // mode-change event.
  controlsOverlay.setMode(rig.mode);

  let manifest;
  try {
    manifest = await loadManifest();
    perfOverlay?.mark('manifest-loaded');
  } catch (err) {
    setStatus(`BLOCKER: could not load terrain manifest — ${(err as Error).message}`);
    console.error(err);
    return;
  }

  // Sun shadow fit + throttle (issue #129). The AOI extent (union of all tile
  // bounds in local space) clamps the camera-following shadow box so it never
  // shadows empty ground beyond the terrain. The rig turns off per-frame shadow
  // auto-update and drives an explicit cadence instead; it is purely cosmetic
  // and never feeds any LOS result (hard constraint #1). If the manifest somehow
  // carries no tiles it is left undefined and the pre-#129 static box stands —
  // a degenerate input degrades to prior behaviour, never a NaN box or a throw
  // (hard constraint #7).
  let shadowRig: ShadowRig | undefined;
  if (manifest.tiles.length > 0) {
    const aoiBounds = manifest.tiles.reduce<AoiBounds>(
      (b, t) => {
        const tb = tileLocalBounds(t);
        return {
          minX: Math.min(b.minX, tb.minX),
          maxX: Math.max(b.maxX, tb.maxX),
          minZ: Math.min(b.minZ, tb.minZ),
          maxZ: Math.max(b.maxZ, tb.maxZ),
        };
      },
      { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity },
    );
    shadowRig = new ShadowRig(lighting.sun, renderer.shadowMap, aoiBounds);
    shadowRig.refit(camera.position.x, camera.position.z);
  }

  let viewpointsFile: ViewpointsFile | undefined;
  try {
    viewpointsFile = await loadViewpoints();
  } catch (err) {
    console.warn('viewpoints unavailable', err);
  }

  // Boot intro (issue #86): overhead hold -> eased sweep -> settled low
  // orbit pose, anchored at viewpoints.json's first entry (read from config,
  // never hardcoded — hard constraint #7). A missing file or an unresolved
  // coordinate there means no intro at all: boot exactly as today (static
  // top-down pose above the scene origin), with the reason logged rather
  // than guessed. Plays in the boot (default) mode — Explore by default
  // (issue #143), which idles identically to Orbit; Fly/Walk/Route are
  // untouched.
  const introAnchorResult = resolveBootIntroAnchor(viewpointsFile);
  let bootIntro: BootIntroSweep | null = null;
  let introAnchor: { x: number; z: number } | null = null;
  if (!introAnchorResult.anchor) {
    console.info(`[ray2] boot intro skipped: ${introAnchorResult.reason}`);
  } else {
    introAnchor = introAnchorResult.anchor;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    // Real sessionStorage, wrapped so a throwing store (private browsing) is
    // treated as "play the intro" by shouldPlayBootIntro rather than crashing
    // boot — same injected-storage pattern as qualityStorage above.
    let introSessionStorage: IntroSessionStorage | null;
    try {
      const s = window.sessionStorage;
      introSessionStorage = { get: (k) => s.getItem(k), set: (k, v) => s.setItem(k, v) };
    } catch {
      introSessionStorage = null;
    }
    if (shouldPlayBootIntro(introSessionStorage, prefersReducedMotion)) {
      bootIntro = new BootIntroSweep({ anchor: introAnchor });
      camera.position.set(bootIntro.startPose.x, bootIntro.startPose.y, bootIntro.startPose.z);
    } else {
      // Reduced motion or a repeat visit this session: skip hold+sweep and
      // boot directly at the settled end pose.
      const endPose = introEndPose(introAnchor, DEFAULT_END_OFFSET_M, DEFAULT_END_Y_AOD);
      camera.position.set(endPose.x, endPose.y, endPose.z);
    }
    // Pivot at the anchor either way (played or skipped) — only the camera's
    // approach to that framing differs. Vertical seating stays owned by the
    // existing seatOrbitTargetIfNeeded (issue #50); this only sets x/z.
    rig.setOrbitPivotXZ(introAnchor.x, introAnchor.z);
  }

  // Cancellation (issue #86): any qualifying user input during hold/sweep
  // hands control back immediately, at whatever pose the intro last applied
  // — "never fight the user". A single window-level, capturing listener set
  // covers every trigger the spec lists (pointer down/drag, wheel, any nav
  // key, mode buttons, the viewpoint select) without per-widget wiring, since
  // all of them begin with a pointerdown/keydown on some element in the page.
  // Listeners are only attached while an intro is actually playing, and torn
  // down the moment it settles (naturally or via cancellation).
  let introCancelRequested = false;
  function cancelBootIntro(): void {
    introCancelRequested = true;
  }
  function removeBootIntroListeners(): void {
    window.removeEventListener('pointerdown', cancelBootIntro, true);
    window.removeEventListener('wheel', cancelBootIntro, true);
    window.removeEventListener('keydown', cancelBootIntro, true);
  }
  if (bootIntro) {
    window.addEventListener('pointerdown', cancelBootIntro, true);
    window.addEventListener('wheel', cancelBootIntro, true);
    window.addEventListener('keydown', cancelBootIntro, true);
  }

  let turbineFile: TurbineScenariosFile | undefined;
  try {
    turbineFile = await loadTurbineScenarios();
  } catch (err) {
    console.warn('turbine scenarios unavailable', err);
  }

  // Route fly-through (docs/plans/2026-07-02-route-flythrough-design.md):
  // community-facing orientation only, interactive/visual — never LOS
  // truth (hard constraint #1). A missing GPX is a reported blocker, not a
  // fabricated route (hard constraint #7), but it does not stop the rest
  // of the viewer from booting.
  let routePolyline: RoutePolyline | undefined;
  try {
    routePolyline = await loadRoute(ROUTE_GPX_URL);
  } catch (err) {
    console.warn('route fly-through unavailable', err);
  }

  // OSM building footprints (docs/plans/2026-07-02-osm-buildings-design.md):
  // scene furniture only — never LOS truth (hard constraint #1). A missing
  // buildings.json logs a warning and the app still boots (hard
  // constraint #7: a missing input is a reported blocker, not a reason to
  // fabricate buildings or halt the rest of the viewer).
  let buildingsFile: BuildingsFile | undefined;
  // Footprint polygons for the "My location" nudge (issue #167), kept as
  // plain local x/z point arrays — NOT three.js meshes — so
  // camera/myLocation.ts (deliberately DOM-free/three.js-free) can stay pure
  // and unit-testable in plain Node; see footprintToLocal in scene/buildings.ts.
  // Computed once here (not per-click), reusing the same loaded corpus the
  // BuildingsLayer renders from rather than a second fetch (viewer has no
  // runtime network deps).
  let buildingFootprints: FootprintRing[] = [];
  try {
    buildingsFile = await loadBuildings();
    buildingFootprints = buildingsFile.buildings.map((b) => footprintToLocal(b.footprint_bng));
  } catch (err) {
    console.warn('buildings unavailable', err);
  }

  // Hand-checked appearance ground truth (issue #146/#157): gates the barn
  // open-front/open-sided void-face rendering (issue #147) so it only applies
  // to classifications independently verified as correct. Optional QA
  // artefact, not LOS truth and not load-bearing for anything else the viewer
  // does -- a missing/unreachable file just means every barn keeps rendering
  // enclosed (an empty verified set), not a boot blocker.
  let appearanceGroundTruth: AppearanceGroundTruth = {};
  try {
    appearanceGroundTruth = await loadAppearanceGroundTruth();
  } catch (err) {
    console.warn('barn appearance ground truth unavailable -- open-front/open-sided barns render enclosed', err);
  }

  // Location beacons (docs/plans/2026-07-02-location-beacons-design.md):
  // orientation furniture only — never LOS truth (hard constraint #1). A
  // missing locations.json is a reported blocker, not a fabricated set of
  // beacons (hard constraint #7): the layer just doesn't appear and the rest
  // of the viewer boots.
  let locationsFile: LocationsFile | undefined;
  try {
    locationsFile = await loadLocations();
  } catch (err) {
    console.warn('location beacons unavailable', err);
  }

  // Radar & aviation bearing lines (issue #171): fixed-altitude orientation
  // furniture only — never LOS truth (hard constraint #1). A missing
  // facilities.json is a reported blocker, not fabricated bearings (hard
  // constraint #7): the layer just doesn't appear and the toggle is disabled.
  let facilitiesFile: FacilitiesFile | undefined;
  try {
    facilitiesFile = await loadFacilities();
  } catch (err) {
    console.warn('radar/aviation bearing lines unavailable', err);
  }

  // OSM water features (issue #25): cosmetic scene furniture only — never LOS
  // truth (hard constraint #1). A missing water.json disables the toggle and
  // the rest of the viewer boots (hard constraint #7).
  let waterFile: WaterFile | undefined;
  try {
    waterFile = await loadWater();
  } catch (err) {
    console.warn('water features unavailable', err);
  }

  // OSM roads (issue #22 walls): landscape furniture only, never LOS truth
  // (hard constraint #1). roads.json drives the dry-stone walls, the road
  // labels, and the scatter keep-off clearance; a missing/unloadable roads.json
  // disables those and the rest of the viewer boots — no fabricated roads (hard
  // constraint #7). The road-surface ribbons load separately from the baked
  // roads_draped.json (below).
  let roadsFile: RoadsFile | undefined;
  try {
    roadsFile = await loadRoads();
  } catch (err) {
    console.warn('roads.json unavailable — road walls and labels disabled', err);
  }

  // Build-time-draped OSM road ribbons (issue #141): the road-surface ribbon
  // mesh is built from roads_draped.json, whose ground heights were baked
  // against the LOD1 TIN by scripts/10_bake_road_drape.py, so the network
  // renders on the first frame(s) with no runtime ground sampling (issue #17).
  // A missing/malformed roads_draped.json disables the ribbon layer with a
  // warning and the viewer still boots (hard constraint #7) — never a fallback
  // to the old, minutes-long runtime drape, never fabricated heights.
  let roadsDrapedFile: RoadsDrapedFile | undefined;
  try {
    const draped = await loadRoadsDraped();
    if (!Array.isArray(draped.roads)) throw new Error('roads_draped.json has no roads array');
    roadsDrapedFile = draped;
  } catch (err) {
    console.warn('roads_draped.json unavailable — OSM road ribbons disabled (run scripts/10_bake_road_drape.py)', err);
  }

  // Carriageway keep-off (issue #79): one shared clearance predicate consumed by
  // every static scatter layer (roadside vegetation, woodland trees, rocks) so
  // nothing plants on the route ribbon or a rendered road ribbon. Roads are
  // pre-clipped to the route corridor; a missing roads.json or missing GPX
  // simply degrades the relevant half of the check (hard constraint #7) — the
  // layers still boot. Scene furniture only, no LOS bearing (hard constraint
  // #1). Every OSM road class (incl. service, now drawn as a ribbon by issue
  // #138) is cleared against, so scatter stays off every rendered ribbon.
  const tarmacClearance = new TarmacClearance({
    roads: roadsFile?.roads ?? null,
    route: routePolyline?.points ?? null,
    corridor: routePolyline?.points ?? null,
  });

  // OSM landcover -> woodland vegetation (issue #56): illustrative scene
  // furniture only, never LOS truth (hard constraint #1). A missing
  // landcover.json disables the vegetation toggle and the rest of the viewer
  // boots (hard constraint #7).
  let landcoverFile: LandcoverFile | undefined;
  try {
    landcoverFile = await loadLandcover();
  } catch (err) {
    console.warn('vegetation (landcover) unavailable', err);
  }

  // Baked procedural trees (issue #58): the near-LOD branched tree meshes. A
  // missing trees.json is a reported blocker, not fabricated geometry (hard
  // constraint #7) — the viewer falls back to billboard-card trees everywhere.
  //
  // Gated on the session's ALLOCATION bucket, not the boot tier (issue #92). The
  // antialias-on bucket sizes every instance buffer to High (`allocQuality`
  // above), so a Medium *boot* — and any live med↔high switch, which never
  // reloads (issue #73) — must have the mesh pools available up front or a later
  // switch to High grows a population that was never allocated. The low-power
  // (antialias-off) bucket keeps its mesh cap at 0 and stays billboard-only
  // (issue #17): the mesh's per-instance triangle cost is skipped there, and a
  // Low↔High switch crosses the antialias boundary and reloads into the right
  // bucket anyway. So the fetch happens whenever the bucket can ever draw a mesh
  // tree (`allocQuality.treeMeshInstanceCap > 0`), which is exactly the
  // antialias-on bucket. The fetch is boot-time, off the render loop — no new
  // per-frame cost and no larger buffers than a High boot already allocates.
  let bakedTrees: BakedTrees | undefined;
  if (allocQuality.treeMeshInstanceCap > 0) {
    try {
      bakedTrees = await loadTrees();
    } catch (err) {
      console.warn('procedural trees unavailable (falling back to billboard cards)', err);
    }
  }

  const tileLoader = new TileLoader(manifest, undefined, { shadows: quality.shadows, tier, preset: shadingPreset });
  scene.add(tileLoader.group);
  rig.groundSampler = (x, z) => tileLoader.collider.sampleGround(x, z);

  if (viewpointsFile) {
    for (const vp of viewpointsFile.viewpoints) {
      const opt = document.createElement('option');
      opt.value = vp.id;
      // viewpointMenuEntry (issue #10) decides label + disabled: estimated
      // settlement/area viewpoints are jumpable but labelled "(approx.)";
      // only genuinely unresolved (null) coordinates stay greyed-out as
      // "coordinates pending" rather than throwing a BLOCKER when clicked.
      const entry = viewpointMenuEntry(vp);
      opt.textContent = entry.label;
      opt.disabled = entry.disabled;
      viewpointSelect.appendChild(opt);
    }
    const pending = viewpointsFile.viewpoints.filter((v) => v.easting === null || v.northing === null).length;
    if (pending > 0) {
      console.info(
        `[ray2] ${pending} of ${viewpointsFile.viewpoints.length} viewpoints have pending coordinates ` +
          '(awaiting community input) and are disabled in the jump menu.',
      );
    }
  }

  await tileLoader.updateAround(0, 0, BOOT_LOAD_RADIUS_M, UNLOAD_RADIUS_M);
  // Blend tile-border normals so the Natural preset shows no lighting seam on
  // the first frames (issue #107); off the render loop, idempotent, self-heals
  // as the horizon streams in on the tile-update tick below.
  tileLoader.reconcileBorderNormals();
  perfOverlay?.mark('boot-tiles-loaded');
  const bootTileStatusToken = setStatus(
    `${tileLoader.loadedTileCount} tile(s) loaded (${manifest.tiles.length} in manifest)`,
  );
  // Fill out to the horizon in the background so distant turbines have ground
  // behind them; the first frame has already painted the near terrain. This
  // promise is intentionally unawaited (fire-and-forget) — only the status
  // write on completion is guarded (issue #84): setStatusIfCurrent only
  // applies it if bootTileStatusToken is still the most recent status commit,
  // i.e. nothing more specific (a jump, a mode switch, a BLOCKER) has been
  // shown in the meantime.
  void tileLoader
    .updateAround(0, 0, LOAD_RADIUS_M, UNLOAD_RADIUS_M)
    .then(() => {
      setStatusIfCurrent(
        bootTileStatusToken,
        `${tileLoader.loadedTileCount} tile(s) loaded (${manifest.tiles.length} in manifest)`,
      );
    });

  // The OSM minimap loads from a lazily-fetched chunk (issue #130): Leaflet is
  // ~150 kB plus its CSS and nothing the first terrain paint needs, so it is
  // dynamic-imported and constructed off the boot critical path (see
  // initMinimap below). `minimapGate` (ui/lazyMinimap.ts) absorbs the
  // render-loop / jump-handler / resize / turbine-swap calls made against the
  // minimap until it attaches — and permanently, harmlessly, if the chunk
  // fails to load (hard constraint #7) — and flushes the current camera pose
  // and turbine set the instant it does, so a deferred minimap opens already
  // synced (never a stale-jump, the issue #81 failure class). Orientation aid
  // only: never read by raypath/, never a LOS input (hard constraint #1).
  const minimapGate = new LazyMinimap();
  const osmMinimapEl = document.getElementById('osm-minimap') as HTMLElement;
  let sinceOsmUpdate = 0;

  // Sheet Map section (issue #132): Recentre re-engages follow the same way
  // the (now compact-hidden) Leaflet recentre control always did — via
  // `minimapGate`, so it degrades exactly like every other minimapGate call
  // while the chunk is still loading (hard constraint #7), no extra
  // plumbing needed.
  document.getElementById('map-recentre-btn')!.addEventListener('click', () => minimapGate.recentre());
  // Ray I (operational) alongside Ray II (issue #43): the resolved scenarios
  // split into the size-variant scenarios that populate the `#turbine-select`
  // dropdown (Ray II scoping + the Ray One turbine-SIZE stand-in — unchanged
  // in spirit from before this unit) and the single real, operational Ray I
  // farm (coordinate_status "osm_asbuilt"), which is never a dropdown option.
  const { sizeVariants: scenarios, operational: operationalScenario } = splitTurbineScenarios(
    turbineFile?.scenarios ?? [],
  );
  // Ray I's real positions, plotted alongside the active size-variant's
  // points below (both farms visible at once on the minimap — issue #43).
  // Fixed for the whole session: Ray I's operational layer never swaps.
  const operationalTurbineMarkers = operationalScenario ? toMarkerPoints(turbineEntities(operationalScenario)) : [];
  const viewpointPoints = viewpointsFile ? viewpointEntities(viewpointsFile.viewpoints) : [];

  // Fetch the minimap chunk and construct/​wire the real map off the critical
  // path (issue #130). Fire-and-forget: boot continues (and the frame loop
  // starts) without awaiting it. The one-time setup that only makes sense once
  // the map exists (compact-signal + bottom-sheet subscriptions, viewpoint
  // markers) is done directly on the real minimap here; the render-loop and
  // interaction calls go through `minimapGate` and are replayed by attach().
  async function initMinimap(): Promise<void> {
    const res = await loadLazy(() => import('./ui/osmMinimap'));
    if (!res.ok) {
      // Degraded-but-booted (hard constraint #7): the minimap is an orientation
      // aid with no LOS bearing, so a failed chunk fetch (offline / captive
      // portal / partial redeploy) is reported, not fatal — navigation, jumps
      // and every 3D layer keep working.
      console.warn('OSM minimap unavailable (offline or blocked chunk?) — navigation still works', res.error);
      setStatus('Minimap unavailable (offline?) — navigation still works.');
      return;
    }
    const minimap = new res.value.OsmMinimap(
      osmMinimapEl,
      {
        onGroundPick: (x, z) => jumpToWorldPoint(x, z),
        onViewpointPick: (id) => {
          const vp = viewpointsFile?.viewpoints.find((v) => v.id === id);
          if (vp) jumpToSavedViewpoint(vp);
        },
        // Expand/collapse exclusivity, direction 1 (issue #110): expanding the
        // minimap closes the bottom sheet. setOpen is a no-op if the sheet is
        // closed or not compact.
        onExpandedChange: (expanded) => {
          if (expanded) mobileSheet.setOpen(false);
        },
      },
      13,
      { locations: locationsFile?.locations ?? [], roads: roadsFile?.roads ?? [] },
    );
    // Compact layout signal (issue #110): sync the already-resolved value, then
    // subscribe for live changes (same pattern as renderCompact above).
    minimap.setCompact(compactSignal.matches);
    compactSignal.subscribe((compact) => minimap.setCompact(compact));
    // Exclusivity, direction 2 (issue #110): the sheet opening collapses an
    // expanded minimap.
    mobileSheet.subscribe((open) => {
      if (open) minimap.setExpanded(false);
    });
    minimap.setViewpoints(toViewpointMarkerPoints(viewpointPoints));
    // Sheet Map section Labels row (issue #132): mirrors the same overlay
    // Leaflet's own (now compact-hidden) layers-control checkbox toggles, so
    // wiring it here — once the real minimap exists — keeps both in sync.
    // Wired only once resolved, same as onGroundPick/onViewpointPick above;
    // disabled with an explanatory title (hard constraint #7) if this
    // dataset resolved no place-name/road-ref labels to show at all.
    const mapLabelsToggle = document.getElementById('map-labels-toggle') as HTMLInputElement;
    if (minimap.hasLabels) {
      mapLabelsToggle.checked = minimap.labelsVisible;
      mapLabelsToggle.addEventListener('change', () => minimap.setLabelsVisible(mapLabelsToggle.checked));
    } else {
      disableLayerToggle(mapLabelsToggle, 'No place-name or road labels available');
    }
    // Read-only e2e handle (issue #75): set only once the map is live, so tests
    // that reach for it wait for the lazy chunk instead of racing it (issue #130).
    (window as unknown as { __ray2_osm_minimap: OsmMinimap }).__ray2_osm_minimap = minimap;
    // Bind the gate last: replays the latest turbine set + camera pose (+ any
    // route-mode recentre) captured during the load window onto the live map.
    minimapGate.attach(minimap);
  }
  void initMinimap();

  const routePlayer = new RoutePlayer();
  let routeRibbon: RouteRibbon | undefined;
  const routeButton = document.querySelector<HTMLButtonElement>('[data-mode="route"]');

  // Turbine geometry can be swapped between size scenarios (Ray II scoping vs
  // Ray One operational) live, at the SAME positions — a familiar size
  // comparison. Only the rendered geometry and the route camera-aim height
  // change; turbine positions and any LOS result are untouched (hard
  // constraint #1).
  let activeScenario: TurbineScenario | undefined = scenarios[0];
  let turbineGroup: Group | undefined;
  let rotorAnimator: RotorAnimator | undefined;
  let routeTurbineTargets: RouteTarget[] = [];
  let routeArrayCentre: RouteTarget | undefined;

  function applyScenario(sc: TurbineScenario): void {
    activeScenario = sc;
    const wasPaused = rotorAnimator?.paused ?? false;
    if (turbineGroup) {
      scene.remove(turbineGroup);
      // Dispose per-turbine geometries; the materials are shared module-level
      // in turbines.ts, so they must NOT be disposed here.
      turbineGroup.traverse((o) => {
        const geom = (o as Mesh).geometry;
        if (geom) geom.dispose();
      });
    }
    const built = buildTurbines(sc);
    turbineGroup = built.group;
    rotorAnimator = built.animator;
    rotorAnimator.paused = wasPaused; // keep the blade toggle state across a swap
    scene.add(turbineGroup);

    // Minimap turbine markers (issue #75): rebuilt on every scenario swap so
    // an eventual size-variant with different positions (today they're all
    // identical) would be reflected; Ray I's operational markers are fixed
    // for the session and passed through unchanged.
    minimapGate.setTurbines(toMarkerPoints(turbineEntities(sc)), operationalTurbineMarkers);

    // Recompute the route camera-aim targets — the hub height changes with the
    // turbine size, so a size swap re-aims the fly-through at the new hubs.
    routeTurbineTargets = turbinePlacements(sc).map((p) => ({
      x: p.x,
      y: p.y + sc.hub_height_m,
      z: p.z,
      label: p.label,
    }));
    routeArrayCentre = routeTurbineTargets.length > 0 ? centroidTarget(routeTurbineTargets) : undefined;
    if (routePolyline && routeArrayCentre) {
      rig.setRoute({
        player: routePlayer,
        polyline: routePolyline,
        arrayCentreTarget: routeArrayCentre,
        turbineTargets: routeTurbineTargets,
      });
    }
    renderAssessmentBasis();
  }

  /** Refreshes the assessment-basis readout (issue #179) from whichever
   * scenario/eye-height state is current — called on every scenario swap and
   * every eye-height change (`setActiveEyeHeight` below), never per frame. */
  function renderAssessmentBasis(): void {
    const basis = formatAssessmentBasis({
      tipHeightM: activeScenario?.tip_height_m,
      hubHeightM: activeScenario?.hub_height_m,
      rotorDiameterM: activeScenario?.rotor_diameter_m,
      source: activeScenario?.source,
      eyeHeightM: activeEyeHeightM,
      eyeHeightIsDefault: activeEyeHeightIsDefault,
    });
    // `basis.text` wraps (style.css) rather than clipping, and its long
    // free-text source citation is already bounded — see the module comment.
    // `basis.full` keeps the untruncated source reachable on hover/focus.
    assessmentBasisEl.textContent = basis.text;
    assessmentBasisEl.title = basis.full;
  }

  /**
   * Updates the eye height both disclosure panels show (issue #178's caption
   * and issue #179's assessment basis) — the single point every viewpoint
   * arrival routes through. `eyeHeightM` is a saved viewpoint's own
   * `eye_height_m`; `undefined` (e.g. a "my location" GPS fix, which carries
   * no such figure) falls back to the documented 1.6 m default and labels it
   * as one, never as though it were that arrival's own stated value (hard
   * constraint #7).
   */
  function setActiveEyeHeight(eyeHeightM: number | undefined): void {
    activeEyeHeightM = eyeHeightM ?? EYE_HEIGHT_M;
    activeEyeHeightIsDefault = eyeHeightM === undefined;
    renderMethodologyCaption();
    renderAssessmentBasis();
  }

  if (activeScenario) applyScenario(activeScenario);

  // Ray I (operational): a NEW, always-on layer at Ray I's real, as-built
  // positions (coordinate_status "osm_asbuilt") — visually distinct grey
  // material (turbines.ts turbineMaterialVariant / the HUD legend below).
  // Built once from RayI_asbuilt and never touched by a size swap: it is not
  // part of the `#turbine-select` dropdown or `applyScenario`'s
  // add/dispose cycle. A missing osm_asbuilt scenario is a reported blocker
  // (hard constraint #7), not a fabricated farm — the layer just doesn't
  // appear.
  let rayIGroup: Group | undefined;
  let rayIAnimator: RotorAnimator | undefined;
  if (operationalScenario) {
    const builtRayI = buildTurbines(operationalScenario);
    rayIGroup = builtRayI.group;
    rayIAnimator = builtRayI.animator;
    scene.add(rayIGroup);
  } else {
    console.warn('Ray I operational farm unavailable (no osm_asbuilt scenario in turbine_scenarios_resolved.json)');
  }

  if (routePolyline && routeArrayCentre) {
    routeRibbon = new RouteRibbon(routePolyline);
    scene.add(routeRibbon.mesh);
    routeRibbon.update((x, z) => tileLoader.collider.sampleGround(x, z));
  } else if (routeButton) {
    // No route data (missing GPX) or no turbine scenario to aim at — do
    // not offer a mode that can't do anything (hard constraint #7).
    routeButton.disabled = true;
    routeButton.title = 'Route fly-through unavailable (missing route data or turbine scenario)';
  }

  // OSM road ribbons (issue #138 render; issue #141 build-time drape): the full
  // road network drawn as one merged tarmac ribbon mesh — service tracks
  // narrower and dirt-brown, public classes at the tarmac width/colour.
  // Orientation-critical and a single merged mesh, so — unlike the low-tier
  // vegetation/stream drops elsewhere — it renders on every quality tier. Built
  // ONCE here from build-time-baked heights (roads_draped.json): the mesh is
  // complete after construction, with no runtime ground sampling and no
  // per-tick update (issue #141 removed the minutes-long runtime drape).
  let roadRibbons: RoadRibbonsLayer | undefined;
  if (roadsDrapedFile) {
    roadRibbons = new RoadRibbonsLayer(roadsDrapedFile);
    scene.add(roadRibbons.mesh);
  }

  // Turbine size selector: swap the rendered turbine geometry between scenarios
  // (only meaningful with more than one).
  const turbineSelect = document.getElementById('turbine-select') as HTMLSelectElement | null;
  if (turbineSelect) {
    if (scenarios.length > 1) {
      for (const sc of scenarios) {
        const opt = document.createElement('option');
        opt.value = sc.id;
        opt.textContent = sc.label;
        turbineSelect.appendChild(opt);
      }
      turbineSelect.value = activeScenario?.id ?? '';
      turbineSelect.addEventListener('change', () => {
        const sc = scenarios.find((s) => s.id === turbineSelect.value);
        if (sc) {
          applyScenario(sc);
          // Swapping the turbine size replaces the shadow casters — force a
          // shadow render so the throttled map picks up the new geometry
          // immediately (issue #129).
          shadowRig?.requestUpdate();
        }
      });
    } else {
      turbineSelect.style.display = 'none';
    }
  }

  let buildingsLayer: BuildingsLayer | undefined;
  let landmarksLayer: LandmarksLayer | undefined;
  const buildingsToggle = document.getElementById('buildings-toggle') as HTMLInputElement | null;
  if (buildingsFile) {
    buildingsLayer = new BuildingsLayer(buildingsFile.buildings, appearanceGroundTruth);
    scene.add(buildingsLayer.group);

    // Landmark asset substitution (issue #124): a bounded, two-entry loader that
    // renders a committed low-poly glTF asset IN PLACE OF the procedural massing
    // for the most recognisable footprints (All Saints church today; the school
    // stays procedural until its reference photo lands). Lazy — off the boot
    // critical path — with the procedural building as the complete fallback if an
    // asset is missing, fails to parse, or is dropped by the quality tier. Scene
    // furniture only, no LOS bearing (hard constraint #1); ground still comes from
    // the collider (#6). Constructed and STARTED before the boot ground pass below
    // so a claimed footprint is suppressed before the procedural version can flash
    // (loadAssets' claim pass is synchronous, before its first await). Assets are
    // dropped on the low tier (#17) — the procedural building renders there.
    landmarksLayer = new LandmarksLayer(buildingsFile.buildings, buildingsLayer, { assetsEnabled: tier !== 'low' });
    scene.add(landmarksLayer.group);
    void landmarksLayer.loadAssets((x, z) => tileLoader.collider.sampleGround(x, z));

    // Unbudgeted first pass, same as pre-#91: this runs once at boot (not on
    // a recurring per-tick path), so a single full scan against the tiles
    // already loaded at boot is a one-off cost, not the repeated per-generation
    // stall the budget exists to prevent (see the frame-loop catchup call
    // below, which IS budgeted). Buildings are keyed by OSM way id, not by
    // proximity to the boot viewpoint, so capping this pass could skip
    // in-range buildings near the boot camera in favour of far-away ones
    // earlier in the array.
    buildingsLayer.update((x, z) => tileLoader.collider.sampleGround(x, z));
    buildingsLayer.updateVisibility(camera.position.x, camera.position.z);
    // Distance-capped shadow casters (issue #128): far buildings drop out of the
    // shadow pass while staying drawn. Med/High only — `quality.shadows` folds the
    // low tier (no shadow pass) into a plain no-op inside the layer.
    buildingsLayer.updateShadowCasters(camera.position.x, camera.position.z, quality.shadows);
    const visible = resolveLayerVisibility({ available: true, persisted: layerVisibility.buildings, defaultVisible: true });
    buildingsLayer.group.visible = visible;
    // The landmark church/school ARE buildings: the same toggle governs both.
    landmarksLayer.setVisible(visible);
    if (buildingsToggle) buildingsToggle.checked = visible;
  } else {
    // Missing buildings.json is a reported blocker, not a fabricated
    // building layer (hard constraint #7) — disable the toggle rather than
    // offering a control that does nothing.
    disableLayerToggle(buildingsToggle, 'Buildings unavailable (missing buildings data)');
  }

  let beaconsLayer: BeaconsLayer | undefined;
  const beaconsToggle = document.getElementById('beacons-toggle') as HTMLInputElement | null;
  if (locationsFile && locationsFile.locations.length > 0) {
    beaconsLayer = new BeaconsLayer(locationsFile.locations);
    scene.add(beaconsLayer.group);
    beaconsLayer.update((x, z) => tileLoader.collider.sampleGround(x, z));
    const visible = resolveLayerVisibility({ available: true, persisted: layerVisibility.beacons, defaultVisible: true });
    beaconsLayer.group.visible = visible;
    if (beaconsToggle) beaconsToggle.checked = visible;
  } else {
    // Missing locations.json is a reported blocker, not fabricated beacons
    // (hard constraint #7) — disable the toggle rather than offering a control
    // that does nothing.
    disableLayerToggle(beaconsToggle, 'Location beacons unavailable (missing locations data)');
  }

  let bearingsLayer: BearingLinesLayer | undefined;
  const bearingsToggle = document.getElementById('bearings-toggle') as HTMLInputElement | null;
  if (facilitiesFile && facilitiesFile.facilities.length > 0) {
    bearingsLayer = new BearingLinesLayer(facilitiesFile.facilities);
    bearingsLayer.setResolution(window.innerWidth, window.innerHeight);
    scene.add(bearingsLayer.group);
    const visible = resolveLayerVisibility({ available: true, persisted: layerVisibility.bearings, defaultVisible: true });
    bearingsLayer.group.visible = visible;
    if (bearingsToggle) bearingsToggle.checked = visible;
  } else {
    disableLayerToggle(bearingsToggle, 'Bearing lines unavailable (missing facilities data)');
  }

  let waterLayer: WaterLayer | undefined;
  const waterToggle = document.getElementById('water-toggle') as HTMLInputElement | null;
  if (waterFile && (waterFile.waterbodies.length > 0 || waterFile.waterways.length > 0)) {
    // On the low quality tier, drop the many small stream ribbons (hundreds of
    // burns) — the loughs + rivers still read as water, at a fraction of the
    // geometry (issue #17). Streams beyond the route corridor are dropped even
    // on high: they are near-route visuals only (perf fix; lakes and rivers
    // are kept everywhere).
    waterLayer = new WaterLayer(waterFile, {
      includeStreams: tier === 'high',
      corridor: routePolyline?.points,
      // High tier: ripple normal samples a texture. Low tier keeps the same
      // animation (vertex/fragment math only) and drops the texture fetch
      // (issue #17).
      rippleTexture: tier === 'high',
    });
    scene.add(waterLayer.group);
    waterLayer.update((x, z) => tileLoader.collider.sampleGround(x, z), GROUND_SAMPLES_PER_TICK);
    const visible = resolveLayerVisibility({ available: true, persisted: layerVisibility.water, defaultVisible: true });
    waterLayer.group.visible = visible;
    if (waterToggle) waterToggle.checked = visible;
  } else {
    disableLayerToggle(waterToggle, 'Water features unavailable (missing water data)');
  }

  let wallsLayer: RoadWallsLayer | undefined;
  const wallsToggle = document.getElementById('walls-toggle') as HTMLInputElement | null;
  if (roadsFile && roadsFile.roads.length > 0) {
    // Walls only near the route (corridor filter): a drystone wall is half a
    // metre wide — beyond a few hundred metres it isn't visually discernible,
    // and dropping distant roads removes most of the ground-sampling work
    // that froze the frame loop.
    wallsLayer = new RoadWallsLayer(roadsFile, { corridor: routePolyline?.points });
    scene.add(wallsLayer.group);
    // Walls are extra geometry; on the low quality tier start hidden so a weak
    // GPU isn't loaded by default, but keep them toggleable (issue #17) — a
    // persisted choice (issue #74) overrides that tier-based default.
    const visible = resolveLayerVisibility({
      available: true,
      persisted: layerVisibility.walls,
      defaultVisible: tier === 'high',
    });
    wallsLayer.group.visible = visible;
    if (wallsLayer.group.visible) {
      wallsLayer.update((x, z) => tileLoader.collider.sampleGround(x, z), GROUND_SAMPLES_PER_TICK);
    }
    if (wallsToggle) wallsToggle.checked = visible;
  } else {
    disableLayerToggle(wallsToggle, 'Road walls unavailable (missing roads data)');
  }

  let vegetationLayer: VegetationLayer | undefined;
  const vegetationToggle = document.getElementById('vegetation-toggle') as HTMLInputElement | null;
  const vegetationAvailable = Boolean(landcoverFile && landcoverFile.features.length > 0);
  const vegetationVisible = resolveLayerVisibility({
    available: vegetationAvailable,
    persisted: layerVisibility.vegetation,
    defaultVisible: true,
  });
  if (vegetationAvailable && landcoverFile) {
    // Woodland vegetation (issue #56) stays on by default on every tier. Its
    // two-regime LOD keeps it in budget: the distant flat canopy over every
    // polygon is near-zero cost, and the near-camera instanced trees are
    // bounded by a global cap (quality.ts's treeInstanceCap) that the low tier
    // sets much lower, with density also scaled down (vegetationDensityScale).
    // Baked branched tree meshes for the near LOD (issue #58), high tier only.
    const treePools = bakedTrees
      ? new TreeMeshPools(bakedTrees, {
          cap: quality.treeMeshInstanceCap,
          maxCap: allocQuality.treeMeshInstanceCap,
        })
      : undefined;
    vegetationLayer = new VegetationLayer(landcoverFile, {
      // Generate candidates at the bucket-max density so a live re-cap has
      // headroom; the active caps (issue #73) are the live GPU-cost lever.
      densityScale: allocQuality.vegetationDensityScale,
      treeInstanceCap: quality.treeInstanceCap,
      maxTreeInstanceCap: allocQuality.treeInstanceCap,
      treePools,
      // Backlight glow (issue #57) tracks the real scene sun rather than the
      // VegetationLayer default: direction the light travels, target - position.
      sunDirection: lighting.sun.target.position.clone().sub(lighting.sun.position),
      clearance: tarmacClearance,
    });
    scene.add(vegetationLayer.group);
    vegetationLayer.group.visible = vegetationVisible;
    vegetationLayer.update(
      (x, z) => tileLoader.collider.sampleGround(x, z),
      GROUND_SAMPLES_PER_TICK,
      tileLoader.collider.generation,
      { x: camera.position.x, z: camera.position.z },
    );
    if (vegetationToggle) vegetationToggle.checked = vegetationVisible;
  } else {
    disableLayerToggle(vegetationToggle, 'Vegetation unavailable (missing landcover data)');
  }

  // Procedural rock/boulder scatter (issue #63): ground-level visual
  // anchoring near the route and the fixed inspection viewpoints. Reuses the
  // same corridor filter vegetation/walls/water share, plus a
  // per-viewpoint radius, so rocks can anchor a standalone viewpoint even
  // with no route corridor nearby. Bounded to a tiny (tens-to-low-hundreds)
  // instance count via quality.rockInstanceCap.
  let rocksLayer: RocksLayer | undefined;
  if ((routePolyline && routePolyline.points.length > 1) || viewpointPoints.length > 0) {
    rocksLayer = new RocksLayer({
      corridor: routePolyline?.points ?? [],
      viewpoints: viewpointPoints,
      instanceCap: quality.rockInstanceCap,
      maxInstanceCap: allocQuality.rockInstanceCap,
      clearance: tarmacClearance,
    });
    scene.add(rocksLayer.group);
    rocksLayer.update((x, z) => tileLoader.collider.sampleGround(x, z), GROUND_SAMPLES_PER_TICK, tileLoader.collider.generation);
  }

  const routePanel = document.getElementById('panel-route')!;
  const routePlayPauseBtn = document.getElementById('route-playpause') as HTMLButtonElement;
  const routeSlowerBtn = document.getElementById('route-slower') as HTMLButtonElement;
  const routeFasterBtn = document.getElementById('route-faster') as HTMLButtonElement;
  const routeResetBtn = document.getElementById('route-reset') as HTMLButtonElement;
  const routeLoopBtn = document.getElementById('route-loop') as HTMLButtonElement;
  const routeAimBtn = document.getElementById('route-aim') as HTMLButtonElement;
  const routeScrubber = document.getElementById('route-scrubber') as HTMLInputElement;
  const routeReadoutEl = document.getElementById('route-readout')!;
  const routeHintToggle = document.getElementById('route-hint-toggle') as HTMLButtonElement;
  const routeHintEl = document.getElementById('route-hint')!;

  // Compact route strip (issue #111): while compact AND Route nav mode are
  // both active, collapses `routePanel` into the single-line
  // `#route-strip` (☰ More + Play/Pause + scrubber + Aim) and moves the
  // remaining controls into the sheet's `#route-sheet-section`. RouteStrip
  // owns `routePanel`'s hidden class entirely from here on — every former
  // `setRoutePanelVisible` call site below becomes `routeStrip.setRouteActive`.
  // `onNarrowMinimapChange` (issue #132) drives the CSS override that reverts
  // `--minimap-compact-width` to its pre-#132 smaller value for as long as
  // the strip doesn't fit alongside the larger one — see routeStrip.ts's
  // class doc for the measurement this responds to.
  const routeStrip = new RouteStrip({
    stripEl: document.getElementById('route-strip') as HTMLElement,
    panelEl: routePanel,
    toggleEl: mobileSheetToggleEl,
    stripItems: [routePlayPauseBtn, routeScrubber, routeAimBtn],
    sheetSectionEl: document.getElementById('route-sheet-section') as HTMLElement,
    sheetItems: [routeSlowerBtn, routeFasterBtn, routeResetBtn, routeLoopBtn, routeHintToggle, routeHintEl, routeReadoutEl],
    minimapEl: osmMinimapEl,
    onNarrowMinimapChange: (narrow) => {
      hudEl.dataset.routeStripNarrowMinimap = String(narrow);
    },
  });
  // Sync the already-resolved compact value immediately (same reasoning as
  // osmMinimap's independent subscription below — routeStrip doesn't exist
  // yet when renderCompact's own compactSignal.subscribe call was wired up).
  routeStrip.setCompact(compactSignal.matches);
  compactSignal.subscribe((compact) => routeStrip.setCompact(compact));

  // Keys hint (issue #76): collapsed behind a `?` toggle instead of always
  // rendering. A native <button> gives Enter/Space activation for free, same
  // pattern as the Layers-drawer toggle above.
  let routeHintOpen = false;
  function renderRouteHint(): void {
    routeHintEl.classList.toggle('hidden', !routeHintOpen);
    routeHintToggle.setAttribute('aria-expanded', String(routeHintOpen));
  }
  renderRouteHint();
  routeHintToggle.addEventListener('click', () => {
    routeHintOpen = !routeHintOpen;
    renderRouteHint();
  });

  if (routePolyline) {
    routeScrubber.max = String(routePolyline.totalLength);
  }

  routePlayPauseBtn.addEventListener('click', () => {
    routePlayer.togglePlay();
    routePlayPauseBtn.textContent = routePlayer.playing ? 'Pause' : 'Play';
  });
  routeSlowerBtn.addEventListener('click', () => routePlayer.setSpeed(routePlayer.speedMultiplier / 1.5));
  routeFasterBtn.addEventListener('click', () => routePlayer.setSpeed(routePlayer.speedMultiplier * 1.5));
  // - / + also change route speed (mirrors the buttons), for trackpad users.
  window.addEventListener('keydown', (e) => {
    if (rig.mode !== 'route') return;
    if (e.code === 'Minus' || e.code === 'NumpadSubtract') routeSlowerBtn.click();
    else if (e.code === 'Equal' || e.code === 'NumpadAdd') routeFasterBtn.click();
  });
  routeResetBtn.addEventListener('click', () => {
    routePlayer.reset();
    routePlayPauseBtn.textContent = routePlayer.playing ? 'Pause' : 'Play';
  });
  routeLoopBtn.addEventListener('click', () => {
    routePlayer.loop = !routePlayer.loop;
    routeLoopBtn.classList.toggle('active', routePlayer.loop);
  });
  const AIM_LABELS: Record<string, string> = {
    'array-centre': 'Array centre',
    nearest: 'Nearest turbine',
    road: 'Road',
    free: 'Free',
  };
  const routeAimLabel = (): string => `Aim: ${AIM_LABELS[routePlayer.aimMode] ?? routePlayer.aimMode}`;

  // Aim button: while free-looking (arrow keys), snap back to the auto-aim;
  // otherwise cycle array-centre <-> nearest turbine (issue #5).
  routeAimBtn.addEventListener('click', () => {
    if (routePlayer.aimMode === 'free') routePlayer.restoreAutoAim();
    else routePlayer.toggleAutoAim();
    routeAimBtn.textContent = routeAimLabel();
  });
  routeScrubber.addEventListener('input', () => {
    if (!routePolyline) return;
    routePlayer.setDistance(Number(routeScrubber.value), routePolyline.totalLength);
  });

  // Minimap click-to-jump (issue #38): move the camera to the clicked world
  // point at ground + eye height. Vertical placement comes only from the
  // collider-backed sampler inside jumpToPoint (hard constraint #6). Purely
  // event-driven — no per-frame cost.
  function jumpToWorldPoint(x: number, z: number): void {
    // Capture before the jump: a successful jumpToPoint switches the rig to
    // explore mode, so rig.mode no longer says where we came from.
    const wasRoute = rig.mode === 'route';
    if (rig.jumpToPoint(x, z)) {
      // Pause the fly-through only on a successful jump (and keep the
      // play/pause label in sync); a failed jump must not disturb route
      // playback at all.
      if (wasRoute) {
        routePlayer.playing = false;
        routePlayPauseBtn.textContent = 'Play';
      }
      const { easting, northing } = localToBng(x, z);
      // Lands in Explore, not fly (issue #158): fly has no toolbar button in
      // the community-facing default UI (#143) and a chained double-click
      // teleport (#156) only works from Explore/Orbit, so landing anywhere
      // else disabled the gesture after one use. Applies identically to both
      // entry points (minimap click and viewport double-click).
      modeButtons.forEach((b) => b.classList.toggle('active', b.dataset.mode === 'explore'));
      controlsOverlay.setMode('explore');
      routeStrip.setRouteActive(false);
      setStatus(
        `Moved to E ${easting.toFixed(0)} N ${northing.toFixed(0)} — explore mode ` +
          '(drag to rotate, WASD to move focus, double-click to jump again).',
      );
      // Refit the shadow box to the jumped-to location and force a render now
      // (issue #129): the box moves only on the tile tick otherwise, so without
      // this a stale shadow from the old location would persist across the
      // teleport until the next tick.
      shadowRig?.refit(camera.position.x, camera.position.z);
      shadowRig?.requestUpdate();
    } else {
      setStatus('No terrain loaded at that point yet — try somewhere closer.');
    }
  }

  // Double-click/double-tap teleport (issue #156): same destination as a
  // minimap click, entered from the main viewport instead. CameraRig detects
  // the qualifying gesture (desktop dblclick / touch double-tap, gated to
  // Explore/Orbit — fly/walk's pointer-lock click is untouched) and hands
  // back client (viewport-pixel) coordinates; this converts those to NDC and
  // raycasts against terrain tile meshes only (buildings/turbines/trees/route
  // ribbon are never registered in the collider, so they cannot win the pick
  // — see TerrainCollider.raycastSurface). A single shared Raycaster: this
  // only ever runs once per gesture, never per frame.
  const surfacePickRaycaster = new Raycaster();
  const surfacePickNdc = new Vector2();
  rig.onSurfaceDoubleClick = (clientX, clientY) => {
    // The boot intro owns the camera until it completes (predictable failure
    // case in #156) — ignored outright, not just deferred.
    if (bootIntro && bootIntro.active) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    surfacePickNdc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    surfacePickRaycaster.setFromCamera(surfacePickNdc, camera);
    const hit = tileLoader.collider.raycastSurface(surfacePickRaycaster);
    // A miss (sky, or scene furniture with no terrain along the ray) is a
    // silent no-op per the issue's predictable-failure table — no status spam.
    if (hit) jumpToWorldPoint(hit.x, hit.z);
  };

  /** A horizontal distance for the status line: metres up close, km once it
   * stops being a number anyone counts in metres. */
  function formatOffsetDistance(metres: number): string {
    return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${metres.toFixed(0)} m`;
  }

  /** What the status line tells the viewer they can now do, per landing mode.
   * Exhaustive over every mode a camera jump can land in — 'route' is excluded
   * because Drive never takes the camera-jump path (see below). */
  function viewpointJumpHint(mode: Exclude<NavMode, 'route'>): string {
    switch (mode) {
      case 'explore':
        return 'explore mode (drag to rotate, WASD to move focus, double-click to jump again).';
      case 'orbit':
        return 'orbit mode (drag to rotate, scroll to zoom).';
      case 'walk':
        return 'walk mode (WASD to walk, click to look).';
      case 'fly':
        return 'fly mode (WASD to move, click to look, -/+ to change speed).';
    }
  }

  // Viewpoint selection (issues #75, #183): shared by the dropdown's change
  // handler and the OSM minimap's viewpoint-marker click, so both entry points
  // behave identically. The nav mode the viewer is already in decides what
  // "go to this viewpoint" means, and is never changed by the selection
  // (issue #183): Drive moves the pre-programmed route playback to the nearest
  // point on the route, every other mode moves the camera to the viewpoint
  // itself. A failure in either path reports and changes nothing — a viewpoint
  // with no resolved coordinate, no terrain under it, or a route that failed
  // to load is a reported blocker, never a fabricated destination (hard
  // constraint #7).
  function jumpToSavedViewpoint(vp: Viewpoint): void {
    const mode = rig.mode;
    try {
      if (mode === 'route') driveToNearestRoutePoint(vp);
      else jumpCameraToViewpoint(vp, mode);
      // Only on success, so a failed marker-driven selection leaves the
      // dropdown showing wherever the viewer actually is.
      viewpointSelect.value = vp.id;
    } catch (err) {
      setStatus(`Cannot jump to ${vp.name}: ${(err as Error).message}`);
      console.error(err);
    }
  }

  /** Non-Drive selection: the camera goes to the viewpoint, in the mode the
   * viewer is already in. */
  function jumpCameraToViewpoint(vp: Viewpoint, mode: Exclude<NavMode, 'route'>): void {
    rig.jumpToViewpoint(vp, mode);
    // A viewpoint with an explicit fov_deg changes camera.fov (issue #95);
    // keep the view-scale button/hint in sync either way (a no-op render
    // when the viewpoint had no fov_deg and the FOV was simply preserved).
    renderViewScale();
    // Disclose THIS viewpoint's own eye height (or the labelled default when
    // it has none) in the methodology/assessment-basis panels (issues
    // #178/#179) — after renderViewScale so a stale eye height never briefly
    // shows against the new framing.
    setActiveEyeHeight(vp.eye_height_m);
    // Same as jumpToWorldPoint: refit + force a shadow render so no stale
    // shadow survives the teleport (issue #129).
    shadowRig?.refit(camera.position.x, camera.position.z);
    shadowRig?.requestUpdate();
    setStatus(`Jumped to ${vp.name} — ${viewpointJumpHint(mode)}`);
  }

  /** Drive selection (issue #183): keep driving, but from the point on the
   * pre-programmed route nearest the viewpoint. Play/pause, speed, loop and
   * aim mode are all deliberately left exactly as they were — only the
   * position along the route moves. The camera follows on the next
   * updateRoute tick, ground-sampled by RoutePlayer as usual (no new
   * ground-fabrication path: an unloaded tile under the snapped point just
   * holds the last sampled eye height, hard constraints #6/#7). */
  function driveToNearestRoutePoint(vp: Viewpoint): void {
    if (vp.easting === null || vp.northing === null) throw new ViewpointCoordinateMissingError(vp.id);
    if (!routePolyline) {
      throw new Error(
        'the pre-programmed route is not loaded, so no point on it can be chosen — the drive is unchanged.',
      );
    }
    const { x, z } = bngToLocal(vp.easting, vp.northing);
    const nearest = nearestPointOnRoute(routePolyline, x, z);
    routePlayer.setDistance(nearest.distanceAlong, routePolyline.totalLength);
    // The route tick rewrites the scrubber every frame anyway; writing it here
    // means the control reflects the snap in the same task as the selection.
    routeScrubber.value = String(routePlayer.distanceAlong);
    // The boot intro has no other way to know it has been pre-empted: this
    // path never changes rig.mode, so the intro's `rig.mode !== bootNavMode`
    // check would not trip and its next tick would overwrite the camera.
    rig.signalJump();
    // Same disclosure update as the non-Drive jump path (issues #178/#179):
    // the viewpoint being driven to is the one whose eye height now applies.
    setActiveEyeHeight(vp.eye_height_m);
    setStatus(
      `Drive: moved to the nearest point on the route to ${vp.name} — ` +
        `${formatOffsetDistance(nearest.offsetM)} from the viewpoint.`,
    );
  }

  // "My location" (issue #161): a one-shot GPS (+ best-effort compass) jump,
  // sourced from a live fix instead of a saved viewpoint, and with its own
  // landing rules — fly (or explore on a touch-only viewport, issue #166),
  // route panel hidden. Deliberately NOT mode-preserving: issue #183 changed
  // saved-viewpoint selection only, and left this control exactly as it was.
  // Hidden outright when the browser exposes no
  // `navigator.geolocation` (issue #161's own requirement) rather than
  // shown-then-disabled.
  if (!geolocationAvailable()) {
    myLocationBtn.classList.add('hidden');
  } else {
    let locating = false;
    myLocationBtn.addEventListener('click', () => {
      // The boot intro owns the camera until it completes — same guard as
      // the double-click teleport and the viewpoint dropdown's entry points.
      if (bootIntro && bootIntro.active) return;
      // A tap while a fix is already in flight is a no-op, not a second
      // concurrent geolocation/orientation request (this is one-shot by
      // design, never a watchPosition loop — issue #161's explicit non-goal).
      if (locating) return;
      locating = true;
      myLocationBtn.disabled = true;
      const previousLabel = myLocationBtn.textContent;
      myLocationBtn.textContent = 'Locating…';
      void jumpToMyLocation().finally(() => {
        locating = false;
        myLocationBtn.disabled = false;
        myLocationBtn.textContent = previousLabel;
      });
    });
  }

  async function jumpToMyLocation(): Promise<void> {
    const positionResult = await readCurrentPosition();
    if (positionResult.kind === 'failure') {
      setStatus(positionResult.message);
      return;
    }
    // Compass access is best-effort and only attempted once a fix exists
    // (issue #161: never block the position jump on missing compass) — no
    // point prompting for orientation permission if there is no position to
    // apply it to.
    const heading = await readCompassHeading();
    const result = poseForMyLocation(
      {
        latitude: positionResult.position.latitude,
        longitude: positionResult.position.longitude,
        accuracyM: positionResult.position.accuracyM,
        headingDeg: heading?.headingDeg,
      },
      rig.groundSampler,
      camera.fov,
      buildingFootprints,
    );
    if (result.kind === 'failure') {
      // outside_aoi (hard constraints #6/#7): camera stays exactly where it was.
      setStatus(result.message);
      return;
    }
    // Touch-only viewports hide Fly/Walk entirely (issue #108) — landing
    // there in fly would strand the user with no working touch controls
    // (issue #166), so land in Explore instead, same as the double-tap
    // teleport (#156/#158). Desktop/pointer viewports keep the previous
    // fly landing. Reuses the same touch-only detection the mode buttons
    // already rely on (`touchOnlySignal`, defined below) rather than a new
    // detection path.
    const landingMode: NavMode = touchOnlySignal.matches ? 'explore' : 'fly';
    rig.jumpToLocatedPose(result.pose, landingMode);
    modeButtons.forEach((b) => b.classList.toggle('active', b.dataset.mode === landingMode));
    controlsOverlay.setMode(landingMode);
    routeStrip.setRouteActive(false);
    renderViewScale();
    // A live GPS fix carries no eye-height figure of its own — revert to the
    // labelled 1.6 m default (issues #178/#179) rather than leaving whatever
    // a previously-selected saved viewpoint had disclosed.
    setActiveEyeHeight(undefined);
    shadowRig?.refit(camera.position.x, camera.position.z);
    shadowRig?.requestUpdate();
    setStatus(result.message);
  }

  modeButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.mode as NavMode;
      if (mode === 'route' && !(routePolyline && routeArrayCentre)) return;
      rig.setMode(mode);
      modeButtons.forEach((b) => b.classList.toggle('active', b === btn));
      controlsOverlay.setMode(mode);
      routeStrip.setRouteActive(mode === 'route');
      // Route mode is the one flow the minimap must stay synced through
      // (issue #41); re-follow in case the user had detached it by panning.
      // Pass the just-jumped camera position directly (issue #81) — rig.setMode
      // above already moved `camera` synchronously, but the minimap's own
      // followState snapshot is only refreshed by the throttled per-frame
      // update() tick, so without this recentre() would briefly centre on the
      // stale pre-jump position and then snap on the next tick.
      if (mode === 'route') minimapGate.recentre({ x: camera.position.x, z: camera.position.z });
    });
  });

  // Touch-only layout signal (issue #108): hides Fly/Walk and grows tap
  // targets via the `#hud[data-touch-only]` CSS rules in style.css.
  // Observed live via matchMedia's change event (never a boot-time-only
  // check), so attaching or removing a trackpad flips the control set
  // without a reload. A missing or throwing `window.matchMedia` (a very old
  // engine) falls back to the full desktop control set — never a blank mode
  // control.
  const touchOnlySignal = createTouchOnlySignal(
    typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : undefined,
  );
  function renderTouchOnly(touchOnly: boolean): void {
    hudEl.dataset.touchOnly = String(touchOnly);
  }
  renderTouchOnly(touchOnlySignal.matches);
  touchOnlySignal.subscribe((touchOnly) => {
    renderTouchOnly(touchOnly);
    // Never strand the user in Fly/Walk once their pointer stops working
    // (predictable failure case: a trackpad unplugged mid-session) — force
    // exactly the same path as an Orbit-button click so active-button state
    // and route-panel visibility stay consistent, rather than duplicating
    // that logic here.
    if (requiresForcedOrbitSwitch(rig.mode, touchOnly)) {
      // Force back to the default (boot) mode's button, which is always
      // rendered — never a hardcoded 'orbit' button that the enabled-modes
      // config may not render (issue #143: Orbit is hidden by default, so
      // clicking an absent button would silently no-op and strand the user).
      // The default mode (Explore/Orbit/Route) is touch-usable in every
      // realistic config; only Fly/Walk are keyboard-only, and they are never
      // the default when they are enabled (they trail Explore in `all`).
      const defaultBtn = Array.from(modeButtons).find((b) => b.dataset.mode === bootNavMode);
      defaultBtn?.click();
    }
  });

  const bladeToggle = document.getElementById('blade-toggle') as HTMLButtonElement | null;
  bladeToggle?.addEventListener('click', () => {
    if (!rotorAnimator && !rayIAnimator) return;
    // Pauses both farms' rotors together — one blade toggle for the whole
    // scene, Ray II (active size-variant) and the always-on Ray I layer alike.
    const paused = !(rotorAnimator?.paused ?? false);
    if (rotorAnimator) rotorAnimator.paused = paused;
    if (rayIAnimator) rayIAnimator.paused = paused;
    bladeToggle.textContent = paused ? 'Blades: paused' : 'Blades: spinning';
    bladeToggle.classList.toggle('active', paused);
    // Force a shadow render so the paused/resumed rotor pose is reflected at
    // once rather than on the next cadence beat (issue #129).
    shadowRig?.requestUpdate();
  });

  buildingsToggle?.addEventListener('change', () => {
    if (!buildingsLayer) return;
    buildingsLayer.group.visible = buildingsToggle.checked;
    landmarksLayer?.setVisible(buildingsToggle.checked);
    persistLayerVisibility('buildings', buildingsToggle.checked);
    // A toggled shadow-caster must not linger in the throttled shadow map
    // (issue #129): force an immediate re-render rather than wait for the
    // next cadence beat.
    shadowRig?.requestUpdate();
  });

  beaconsToggle?.addEventListener('change', () => {
    if (!beaconsLayer) return;
    beaconsLayer.group.visible = beaconsToggle.checked;
    persistLayerVisibility('beacons', beaconsToggle.checked);
    shadowRig?.requestUpdate();
  });

  bearingsToggle?.addEventListener('change', () => {
    if (!bearingsLayer) return;
    bearingsLayer.group.visible = bearingsToggle.checked;
    persistLayerVisibility('bearings', bearingsToggle.checked);
    shadowRig?.requestUpdate();
  });

  waterToggle?.addEventListener('change', () => {
    if (!waterLayer) return;
    waterLayer.group.visible = waterToggle.checked;
    persistLayerVisibility('water', waterToggle.checked);
    shadowRig?.requestUpdate();
  });

  /** Set when scenery layers (walls/water) still have budgeted ground-sampling
   * work to finish, so the tick keeps calling them even with no new tiles. */
  let sceneryCatchup = false;

  wallsToggle?.addEventListener('change', () => {
    if (!wallsLayer) return;
    wallsLayer.group.visible = wallsToggle.checked;
    // Hidden walls skip ground sampling entirely, so catch up now: mark the
    // scenery drape dirty and let the budgeted per-tick updates fill in any
    // terrain that streamed in while the layer was off.
    if (wallsLayer.group.visible) sceneryCatchup = true;
    persistLayerVisibility('walls', wallsToggle.checked);
    shadowRig?.requestUpdate();
  });

  vegetationToggle?.addEventListener('change', () => {
    if (!vegetationLayer) return;
    const on = vegetationToggle.checked;
    vegetationLayer.group.visible = on;
    if (on) sceneryCatchup = true;
    persistLayerVisibility('vegetation', on);
    shadowRig?.requestUpdate();
  });

  viewpointSelect.addEventListener('change', () => {
    const vp = viewpointsFile?.viewpoints.find((v) => v.id === viewpointSelect.value);
    if (!vp) return;
    jumpToSavedViewpoint(vp);
  });

  function resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    // Re-evaluate the effective pixel ratio against the current tier's cap
    // (issue #73): resize also fires when the window is dragged between monitors
    // of different devicePixelRatio, so honour the cap here rather than only at
    // boot / on a quality switch.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, quality.pixelRatioCap));
    renderer.setSize(w, h);
    // The composer/bloom are guarded: they are undefined until the lazy
    // postprocessing chunk resolves (issue #130), and stay undefined if it
    // fails to load. EffectComposer.setSize resets every pass (including
    // bloomPass) to the full composer resolution, so the half-res budget from
    // BLOOM_RESOLUTION_SCALE must be re-applied after it on every resize.
    const pr = renderer.getPixelRatio();
    if (composer) {
      composer.setPixelRatio(pr);
      composer.setSize(w, h);
    }
    bloomPass?.setSize(
      Math.round(w * pr * BLOOM_RESOLUTION_SCALE),
      Math.round(h * pr * BLOOM_RESOLUTION_SCALE),
    );
    camera.aspect = w / h;
    // Official sheet is defined by its HORIZONTAL field (issue #177), so a
    // window that changes shape needs a new vertical FOV to keep the horizon
    // width at 90°. Layout-time only — never per frame — and it re-labels the
    // button (and re-clamps, on a sliver of a window) in the same pass. The
    // other two presets are fixed FOVs and are deliberately left alone.
    if (viewScalePreset === 'official-sheet') {
      camera.fov = fovForPreset('official-sheet', camera.aspect);
      renderViewScale();
    }
    camera.updateProjectionMatrix();
    // The bar spans the full viewport width, so its tick positions are only
    // correct against `w` itself; it is never measured from the DOM (repo perf
    // rule: no layout reads on the render loop).
    scaleBarWidthPx = w;
    // Fat bearing lines need their pixel width re-projected at the new viewport
    // size (they carry a `resolution` uniform); a no-op when the layer is absent.
    bearingsLayer?.setResolution(w, h);
    // Re-syncs Leaflet's size cache with its CSS box, which vw/vh-based
    // compact sizing (issue #110) can change on this same resize/rotation
    // without Leaflet otherwise finding out — the "no blank tile strips"
    // failure case. Cheap; harmless when the minimap's box didn't actually
    // change (e.g. a desktop window resize).
    minimapGate.invalidateSize();
    // Re-decides the route strip's width fallback (issue #132) at the new
    // viewport size — a no-op while inactive. Layout-time only, same
    // reasoning as invalidateSize() above, never per frame.
    routeStrip.refreshFit();
  }
  window.addEventListener('resize', resize);
  resize();

  // Segmented quality control (issue #73): Auto | Low | Med | High. Everything
  // the live renderer allows (shadows, shadow map size, pixel-ratio cap, bloom,
  // and the four instance caps) applies in place via applyQualitySettings; a
  // switch that crosses the MSAA boundary (low ↔ med/high) persists the choice
  // and reloads, since MSAA is fixed at context creation. Precedence /
  // persistence live in quality.ts (issue #72); this only wires the UI to them.
  let prevShadowsEnabled = quality.shadows;
  const applyTarget: QualityApplyTarget = {
    setShadowsEnabled(on) {
      renderer.shadowMap.enabled = on;
      lighting.sun.castShadow = on;
      if (on !== prevShadowsEnabled) {
        prevShadowsEnabled = on;
        // Materials must recompile to add/remove shadow sampling when shadows flip.
        scene.traverse((o) => {
          const m = (o as { material?: { needsUpdate: boolean } | Array<{ needsUpdate: boolean }> }).material;
          if (Array.isArray(m)) m.forEach((mm) => (mm.needsUpdate = true));
          else if (m) m.needsUpdate = true;
        });
      }
    },
    setShadowMapSize(size) {
      const shadow = lighting.sun.shadow;
      if (shadow.mapSize.width === size) return;
      shadow.mapSize.set(size, size);
      // Drop the existing shadow map render target so three.js rebuilds it at the
      // new size on the next render (the only way to resize a live shadow map).
      shadow.map?.dispose();
      shadow.map = null;
    },
    setPixelRatioCap(cap) {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap));
      // composer/bloom may not have loaded yet (issue #130): guard both, so a
      // live quality switch during the lazy-load window is a safe no-op on them
      // and re-applies to the composer once it exists (via resize()).
      composer?.setPixelRatio(renderer.getPixelRatio());
    },
    setBloom(on) {
      if (bloomPass) bloomPass.enabled = on && postEnabled;
    },
    setTreeMeshCap(cap) {
      vegetationLayer?.setTreeMeshCap(cap);
    },
    setVegetationCap(cap) {
      vegetationLayer?.setInstanceCap(cap);
    },
    setRockCap(cap) {
      rocksLayer?.setInstanceCap(cap);
    },
  };

  /** Re-realises the near-camera instance layers under the current caps, off the
   * render loop, reusing each layer's cached instances + ground (no regeneration,
   * no re-raycast). One pass is enough: already-grounded instances re-claim slots
   * without spending the sample budget. */
  function reconcileQualityLayers(): void {
    const sampler = (x: number, z: number) => tileLoader.collider.sampleGround(x, z);
    const gen = tileLoader.collider.generation;
    const cam = { x: camera.position.x, z: camera.position.z };
    vegetationLayer?.update(sampler, GROUND_SAMPLES_PER_TICK, gen, cam);
    rocksLayer?.update(sampler, GROUND_SAMPLES_PER_TICK, gen);
  }

  /** Applies a tier live (no reload): a single synchronous pass over its settings
   * (so a context loss mid-switch can't leave a half-applied tier), then a resize
   * to re-apply pixel ratio / bloom half-res, then the layer reconcile. */
  function applyQualityLive(next: QualityTier): void {
    tier = next;
    quality = qualitySettings(next);
    hudEl.dataset.qualityTier = tier;
    applyQualitySettings(applyTarget, quality);
    resize();
    reconcileQualityLayers();
    // Force a shadow render after the switch (issue #129): a Low→Med/High
    // transition re-enables shadows, and a Med↔High transition disposes the
    // shadow map to resize it — both need an immediate re-render under the
    // throttled cadence, not a wait for the next beat. quality.shadows is now
    // the target tier's value, so tick() resumes correctly from here.
    shadowRig?.requestUpdate();
  }

  const qualityButtons = new Map<QualityChoice, HTMLButtonElement>();
  for (const choice of QUALITY_CHOICES) {
    const btn = document.getElementById(`quality-${choice}`) as HTMLButtonElement | null;
    if (btn) qualityButtons.set(choice, btn);
  }
  let currentChoice: QualityChoice = initialChoice({ urlOverride, savedChoice });

  function renderQualityControl(): void {
    const autoBtn = qualityButtons.get('auto');
    if (autoBtn) autoBtn.textContent = choiceLabel('auto', autoDetectedTier);
    for (const [choice, btn] of qualityButtons) {
      btn.classList.toggle('active', choice === currentChoice);
    }
  }

  function selectQuality(choice: QualityChoice): void {
    const targetTier = choiceTier(choice, autoDetectedTier);
    const targetAntialias = qualitySettings(targetTier).antialias;
    // Persist the choice (or clear it for Auto) BEFORE deciding reload vs live:
    // a reload can only carry the switch if the choice survives the reload.
    const storageOk = choice === 'auto' ? removeSavedChoice() : saveQualityTier(qualityStorage, choice);
    const plan = planQualitySwitch({ currentAntialias: contextAntialias, targetAntialias, storageOk });
    currentChoice = choice;
    if (plan.action === 'reload') {
      location.reload();
      return;
    }
    // 'live' or 'live-partial': apply every non-antialias setting in place. In
    // the partial case the antialias can't change (no reload possible), so we
    // report that honestly rather than pretending it took (hard constraint #7).
    applyQualityLive(targetTier);
    renderQualityControl();
    if (plan.action === 'live-partial' && plan.message) {
      setStatus(plan.message);
    }
  }

  for (const [choice, btn] of qualityButtons) {
    btn.addEventListener('click', () => selectQuality(choice));
  }
  renderQualityControl();
  console.info(
    `[ray2] quality tier: ${tier}` +
      `${urlOverride ? ' (url override)' : savedChoice ? ' (saved choice)' : ' (auto-detected)'}`,
  );

  // Shading preset control (issue #106): Stylised | Natural. Always live —
  // there is no antialias-style reload boundary on this axis — so unlike
  // selectQuality above there is no reload/live-partial branching.
  const shadingButtons = new Map<ShadingPreset, HTMLButtonElement>();
  for (const choice of SHADING_CHOICES) {
    const btn = document.getElementById(`shading-${choice}`) as HTMLButtonElement | null;
    if (btn) shadingButtons.set(choice, btn);
  }

  function renderShadingControl(): void {
    for (const [choice, btn] of shadingButtons) {
      btn.textContent = shadingLabel(choice);
      btn.classList.toggle('active', choice === shadingPreset);
    }
  }

  function selectShadingPreset(preset: ShadingPreset): void {
    shadingPreset = preset;
    saveShadingPreset(qualityStorage, preset);
    hudEl.dataset.shadingPreset = preset;
    tileLoader.setShadingPreset(preset);
    renderShadingControl();
  }

  for (const [choice, btn] of shadingButtons) {
    btn.addEventListener('click', () => selectShadingPreset(choice));
  }
  renderShadingControl();
  console.info(
    `[ray2] shading preset: ${shadingPreset}` +
      `${shadingUrlOverride ? ' (url override)' : shadingSavedChoice ? ' (saved choice)' : ' (default)'}`,
  );

  const forward = new Vector3();
  let last = performance.now();
  let sinceTileUpdate = 0;
  /** -1 forces the first tick to drape scenery even if no tile has changed
   * since boot (the constructors' budgeted first pass may be incomplete). */
  let lastColliderGeneration = -1;
  // Boot-intro cancellation (issue #158): a successful jumpToPoint bumps
  // rig.jumpSeq, the only signal CameraRig can give main.ts that a jump just
  // happened without CameraRig knowing anything about the intro — a jump
  // landing in Explore (the intro's own boot mode) no longer trips the
  // `rig.mode !== bootNavMode` defence-in-depth check below on its own, so
  // without this a jump mid-intro would get silently overwritten by the
  // intro's next tick. Also catches jumps made directly against the rig
  // (bypassing jumpToWorldPoint entirely, e.g. test automation), not just
  // ones routed through the minimap/double-click UI.
  let lastSeenJumpSeq = rig.jumpSeq;

  function frame(now: number): void {
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;
    perfOverlay?.markOnce('first-frame');
    // renderer.info reflects the previous completed render() call; sampling
    // it here pairs each reading with this tick's frame-time delta.
    perfOverlay?.sample(renderer.info, dt * 1000, now);

    // Boot intro (issue #86): forces the camera pose for this frame while
    // the intro is active, then falls through to the normal rig.update()
    // below — OrbitControls recomputes its look-at from whatever position we
    // just set (no user input this frame means no rotation delta, so it
    // reproduces our forced position and simply re-aims at the grounded
    // pivot), and the existing camera floor (issue #50) still applies
    // afterwards, exactly as it would to any other orbit-mode frame. Any
    // mode switch away from orbit (however triggered) also ends the intro's
    // control, as a defence-in-depth alongside the DOM-event cancellation.
    if (bootIntro && bootIntro.active) {
      // The intro plays in the boot (default) mode and hands control back the
      // moment the user switches to a DIFFERENT mode (issue #143: the default
      // is Explore now, not Orbit — keying this off a hardcoded 'orbit' would
      // cancel the intro on frame 1). Explore idles identically to Orbit (same
      // OrbitControls update with no keys held), so the intro's forced poses
      // carry through unchanged.
      const jumped = rig.jumpSeq !== lastSeenJumpSeq;
      lastSeenJumpSeq = rig.jumpSeq;
      const cancelRequested = introCancelRequested || rig.mode !== bootNavMode || jumped;
      const groundAvailable = introAnchor !== null && tileLoader.collider.sampleGround(introAnchor.x, introAnchor.z) !== null;
      const applied = bootIntro.tick(dt, { groundAvailable, cancelRequested });
      if (applied) camera.position.set(bootIntro.pose.x, bootIntro.pose.y, bootIntro.pose.z);
      if (!bootIntro.active) {
        removeBootIntroListeners();
        // The rig boots straight into Explore (issue #143), so setMode never ran
        // Explore's azimuth polar clamp. Assert it now — but ONLY when the user
        // cancelled the intro (they are driving, often from the near-overhead
        // hold pose where ←/→ would otherwise be dead). A natural settle leaves
        // the low-orbit end pose untouched: that pose sits outside the clamp, so
        // applying it there would nudge the settled framing the intro produced.
        if (introCancelRequested) rig.applyExploreClampIfActive();
      }
    }
    rig.update(dt);
    // Recentre the sky dome on the camera (issue #60): one position set, no
    // allocation, so the dome always fully encloses the visible scene no
    // matter how far the camera roams from the local origin.
    skyDome.update(camera.position.x, camera.position.y, camera.position.z);
    // Camera-distance-aware fog thinning (issue #175): radial FogExp2 density
    // keyed on camera-to-active-array distance; base density when turbines absent.
    if (routeArrayCentre) {
      const cameraToArrayM = Math.hypot(
        camera.position.x - routeArrayCentre.x,
        camera.position.y - routeArrayCentre.y,
        camera.position.z - routeArrayCentre.z,
      );
      updateDistanceFadeDensity(scene, cameraToArrayM);
    } else {
      updateDistanceFadeDensity(scene, FOG_REFERENCE_DISTANCE_M);
    }
    rotorAnimator?.update(dt);
    rayIAnimator?.update(dt);
    // Ripple animation (issue #65): a single time-uniform bump, no ground
    // sampling or geometry rebuild — cheap enough to run every frame.
    waterLayer?.tick(dt);
    bearingsLayer?.tick(dt);

    sinceTileUpdate += dt;
    if (sinceTileUpdate >= TILE_UPDATE_INTERVAL_S) {
      sinceTileUpdate = 0;
      void tileLoader.updateAround(camera.position.x, camera.position.z, LOAD_RADIUS_M, UNLOAD_RADIUS_M).then(() => {
        // Re-resolve any ribbon vertices / building bases whose ground has
        // just streamed in (design doc "Data flow" step 4) — self-heals as
        // tiles load. Gated on the tile set actually changing: re-sampling on
        // every tick regardless is what froze the frame loop (the walls/water
        // 2 s animate/freeze cycle). Walls/water also cache per-vertex and
        // spend at most GROUND_SAMPLES_PER_TICK raycasts per call, reporting
        // whether they need a catch-up call next tick.
        const generation = tileLoader.collider.generation;
        const sample = (x: number, z: number) => tileLoader.collider.sampleGround(x, z);

        // Vegetation (issue #56) reconciles around the CAMERA every tick, not
        // just when tiles change: trees realise/release as you move through the
        // AOI even within a single tile generation. Its ground-sampling stays
        // bounded (canopy + near instances share GROUND_SAMPLES_PER_TICK) and
        // once settled it costs only the cheap per-tick reconcile.
        if (vegetationLayer && vegetationLayer.group.visible) {
          vegetationLayer.update(sample, GROUND_SAMPLES_PER_TICK, generation, {
            x: camera.position.x,
            z: camera.position.z,
          });
        }
        // Rocks (issue #63) are a bounded, one-off scatter with no
        // camera-distance reconcile (see rocks.ts) — just keep grounding any
        // instance whose ground hadn't streamed in yet, within the same
        // per-tick sample budget.
        rocksLayer?.update(sample, GROUND_SAMPLES_PER_TICK, generation);

        if (generation !== lastColliderGeneration || sceneryCatchup) {
          lastColliderGeneration = generation;
          // Reconcile the border normals of tiles that just streamed in with
          // their neighbours (issue #107) so the Natural preset shows no
          // lighting seam at tile edges. Event-driven (only when the tile set
          // changed), idempotent, and never per-frame; touches normals/colours
          // only, so the collider and LOS results are unaffected.
          tileLoader.reconcileBorderNormals();
          routeRibbon?.update(sample);
          beaconsLayer?.update(sample);
          let catchup = false;
          if (buildingsLayer) {
            catchup = buildingsLayer.update(sample, GROUND_SAMPLES_PER_TICK, generation) || catchup;
          }
          // Landmark assets self-heal onto streamed ground on the same tick as
          // the procedural buildings (issue #124), hidden until their terrain
          // arrives — never floating/buried (hard constraint #6).
          if (landmarksLayer) {
            catchup = landmarksLayer.update(sample, GROUND_SAMPLES_PER_TICK, generation) || catchup;
          }
          if (waterLayer) catchup = waterLayer.update(sample, GROUND_SAMPLES_PER_TICK, generation) || catchup;
          if (wallsLayer && wallsLayer.group.visible) {
            catchup = wallsLayer.update(sample, GROUND_SAMPLES_PER_TICK, generation) || catchup;
          }
          // Road ribbons (issue #141) are built once from baked heights — no
          // runtime drape, so nothing to catch up on the tile-update tick.
          sceneryCatchup = catchup;
        }
        // Draw-call guardrail (issue #91): pure distance check, no ground
        // sampling, so it runs on every scenery tick (not gated on the tile
        // generation changing) — the camera keeps moving even when no new
        // tile has streamed in. Never runs per-frame (see frame() above).
        buildingsLayer?.updateVisibility(camera.position.x, camera.position.z);
        // Shadow-caster cap (issue #128): same tick, same pure-distance
        // discipline. `quality.shadows` (updated live by applyQualityLive on a
        // tier switch) makes this a no-op on the low tier. A viewpoint teleport
        // is re-evaluated here on the next tick, no camera movement required.
        buildingsLayer?.updateShadowCasters(camera.position.x, camera.position.z, quality.shadows);
        // Shadow box fit (issue #129): slide the camera-following ortho box to
        // wherever the camera now is, clamped to the AOI. Position-only, no
        // resize — same tick-only discipline as the caster cap above (repo perf
        // rule: never per frame). A teleport refits immediately in its handler.
        shadowRig?.refit(camera.position.x, camera.position.z);
      });
    }

    camera.getWorldDirection(forward);
    const readout = computeReadout(camera.position.x, camera.position.y, camera.position.z, forward.x, forward.z, camera.fov);
    // Skip the DOM write while `#hud-strip` (readout + status) is hidden
    // inside a closed compact-layout sheet (issue #109) — nothing reads a
    // write that lands on a hidden node, and this is the render loop (repo
    // performance rule: no wasted per-frame DOM writes). `readout` itself is
    // still computed unconditionally: `readout.bearingDeg` feeds the OSM
    // minimap below regardless of the strip's visibility.
    if (mobileSheet.hudStripVisible) {
      readoutEl.textContent = formatReadout(readout);
    }

    // Angular scale bar (issue #177): only drawn under the official-sheet
    // preset, and it decides for itself whether the bearing has moved enough
    // to be worth a redraw (throttled, pooled nodes, no layout reads) — so
    // this call costs a couple of comparisons on every other frame. Reuses
    // `readout.bearingDeg`, already computed above, rather than a second
    // bearing derivation. Presentation only (hard constraint #1).
    angularScaleBar.update(
      viewScalePreset === 'official-sheet',
      readout.bearingDeg,
      officialSheetHfovDeg,
      scaleBarWidthPx,
      now,
    );

    // OSM follow-map: recentre a few times a second (Leaflet setView is not
    // free, and per-frame is imperceptibly smoother but wasteful).
    sinceOsmUpdate += dt;
    if (sinceOsmUpdate >= 0.2) {
      sinceOsmUpdate = 0;
      minimapGate.update(camera.position.x, camera.position.z, readout.bearingDeg);
    }

    if (rig.mode === 'route' && routePolyline) {
      const distM = routePlayer.distanceAlong;
      const totalM = routePolyline.totalLength;
      routeScrubber.value = String(distM);
      routePlayPauseBtn.textContent = routePlayer.playing ? 'Pause' : 'Play';
      routeLoopBtn.classList.toggle('active', routePlayer.loop);
      // Reflect free-look vs auto-aim in the button so the state is visible when
      // the viewer grabs the camera with the arrow keys (issue #5).
      routeAimBtn.textContent = routeAimLabel();
      routeAimBtn.classList.toggle('active', routePlayer.aimMode === 'free');

      // #route-readout skip-while-hidden (issue #111, mirrors #109's
      // `mobileSheet.hudStripVisible` pattern): while compact+Route is
      // active, #route-readout lives in the sheet's route section and is
      // only reachable while the sheet is open — skip the nearest-turbine
      // scan and the textContent write while it would land on a hidden node.
      if (!routeStrip.isActive || mobileSheet.isOpen) {
        let nearestLabel = '';
        let nearestDistM = Infinity;
        for (const t of routeTurbineTargets) {
          const d = Math.hypot(t.x - camera.position.x, t.z - camera.position.z);
          if (d < nearestDistM) {
            nearestDistM = d;
            nearestLabel = t.label ?? '';
          }
        }
        const nearestText = Number.isFinite(nearestDistM) ? `${nearestLabel} ${nearestDistM.toFixed(0)} m` : 'n/a';
        routeReadoutEl.textContent =
          `km ${(distM / 1000).toFixed(1)} / ${(totalM / 1000).toFixed(1)}  |  ` +
          `distance ${distM.toFixed(0)} m  |  nearest turbine ${nearestText}`;
      }
    }

    // Sun shadow cadence (issue #129): flag the shadow map for a render on the
    // cadence beats only, so most frames skip the whole shadow pass. A no-op
    // when shadows are off (low tier). Purely cosmetic — never a LOS input.
    shadowRig?.tick(quality.shadows);

    // Render through the postprocessing composer once its lazy chunk has
    // resolved (issue #130); until then — and permanently if the chunk failed
    // to load — render straight to the canvas (AgX + MSAA, no bloom/grade).
    if (composer) composer.render();
    else renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }
  // Load the postprocessing chunk and build the composer now — after every
  // boot declaration exists (its resize() call reaches minimapGate et al.) and
  // after the initial resize() above. Fire-and-forget: the frame loop starts
  // immediately and renders via renderer.render() until the chunk resolves
  // (issue #130).
  void initPostprocessing();
  requestAnimationFrame(frame);

  // Small hook for headless (Playwright) tests to introspect the scene
  // graph without duck-typing DOM state — mirrors the read-only-diagnostic
  // pattern already used by console.info above, just queryable at runtime.
  (window as unknown as { __ray2_scene: Scene }).__ray2_scene = scene;
  // Camera + rig handles for the app-level camera-floor / grounded-pivot e2e
  // (issue #52): the test reads the live camera pose and calls the rig's
  // collider-backed groundSampler. Read-only diagnostic, same pattern as
  // __ray2_scene / __ray2_renderer above; never read by LOS code.
  (window as unknown as { __ray2_camera: PerspectiveCamera }).__ray2_camera = camera;
  (window as unknown as { __ray2_rig: CameraRig }).__ray2_rig = rig;
  // Boot intro handle (issue #86): read-only diagnostic, same pattern as
  // __ray2_scene/__ray2_camera above — lets headless e2e poll `.active`/`.phase`
  // deterministically instead of racing the intro's own hold/sweep timers
  // against network/tile-load latency. Null whenever no intro is playing
  // (reduced motion, repeat visit this session, or no resolved anchor).
  (window as unknown as { __ray2_boot_intro: BootIntroSweep | null }).__ray2_boot_intro = bootIntro;
  // The OSM minimap e2e handle (issue #75, __ray2_osm_minimap) is set inside
  // initMinimap() once its lazy chunk resolves (issue #130), not here — a test
  // that reaches for it waits for the map to exist rather than racing the
  // dynamic import.

  console.log(
    `[ray2] local origin E ${LOCAL_ORIGIN_EASTING} N ${LOCAL_ORIGIN_NORTHING}; ` +
      'mesh raycasts are interactive feedback only, never LOS truth.',
  );
}

function buildLayout(enabledNavModes: readonly NavMode[]): string {
  return `
    <canvas id="scene-canvas"></canvas>
    <div id="hud">
      <div id="hud-top">
        <div id="top-bar" class="card">
          <span id="mode-control" class="segmented" role="group" aria-label="Navigation mode">${navModeButtonsHtml(enabledNavModes)}</span>
          <select id="viewpoint-select">
            <option value="">Jump to viewpoint&hellip;</option>
          </select>
          <!-- Inline top-bar control on desktop (issue #168 follow-up). Deliberately
               absent from MobileSheet's items so the sheet never swallows it; on
               touch viewports it is lifted out of flow via position:fixed under the
               #hud[data-touch-only='true'] #my-location-btn CSS rule so it stays
               visible top-right regardless of #top-bar's touch styling. Its id,
               click handler, busy-state textContent swap, and the
               geolocationAvailable() === false -> .hidden gate in boot() are all
               unchanged. -->
          <button id="my-location-btn" type="button" title="Jump the camera to your GPS position" aria-label="My location">My location</button>
          <span id="quality-control" class="segmented" role="group" aria-label="Rendering quality" title="Auto detects your GPU; Low / Med / High override it. Switching antialiasing on or off (Low ↔ Med/High) reloads the page and restarts a fly-through from the boot view; every other change applies instantly and keeps playback running.">
            <button id="quality-auto" type="button" data-quality="auto">Auto</button>
            <button id="quality-low" type="button" data-quality="low">Low</button>
            <button id="quality-medium" type="button" data-quality="medium">Med</button>
            <button id="quality-high" type="button" data-quality="high">High</button>
          </span>
          <span id="shading-control" class="segmented" role="group" aria-label="Terrain shading style" title="Stylised keeps today's faceted look; Natural smooths the terrain and blends colour continuously. Applies instantly, keeps any fly-through running.">
            <button id="shading-stylised" type="button" data-shading="stylised">Stylised</button>
            <button id="shading-natural" type="button" data-shading="natural">Natural</button>
          </span>
          <button id="view-scale-toggle" type="button" title="Cycle framing: true scale (50mm) → explore → official sheet (90° horizontal / 21mm, the wide context view used on wireline sheets — not true scale) (V)">View: true scale (50mm)</button>
          <button id="layers-toggle" type="button" aria-expanded="false" aria-controls="layers-drawer">&#9776; Layers</button>
        </div>
        <div id="layers-drawer" class="card hidden">
          <label class="layer-row"><input type="checkbox" id="buildings-toggle" checked /><span>Buildings</span></label>
          <label class="layer-row"><input type="checkbox" id="water-toggle" checked /><span>Water</span></label>
          <label class="layer-row"><input type="checkbox" id="walls-toggle" /><span>Walls</span></label>
          <label class="layer-row"><input type="checkbox" id="vegetation-toggle" checked /><span>Vegetation</span></label>
          <label class="layer-row"><input type="checkbox" id="beacons-toggle" checked /><span>Beacons</span></label>
          <label class="layer-row"><input type="checkbox" id="bearings-toggle" checked /><span>Bearings</span></label>
          <button id="blade-toggle" type="button">Blades: spinning</button>
          <select id="turbine-select" title="Swap the turbine size at the same positions"></select>
          <div id="turbine-legend" title="Ray I renders always-on at its real positions; Ray II is the size-selectable proposed layout above">
            <span class="legend-item"><span class="legend-swatch legend-swatch-proposed"></span>Proposed &mdash; Ray II</span>
            <span class="legend-item"><span class="legend-swatch legend-swatch-operational"></span>Operational &mdash; Ray I</span>
          </div>
        </div>
        <div id="controls-overlay-wrap">
          <button id="controls-toggle" type="button" aria-expanded="true" aria-controls="controls-overlay" aria-label="Toggle controls legend" title="Toggle controls legend">&#9000;</button>
          <div id="controls-overlay" class="card" role="note" aria-label="Keyboard and mouse controls for the current navigation mode"></div>
        </div>
      </div>
      <!-- Horizontal angular scale bar (issue #177): spans the full viewport
           width so its degree ticks line up with what is actually on screen,
           and is shown only while the official-sheet framing preset is
           active. Sits outside #hud-bottom deliberately — that stack is
           inset 12px, which would offset every tick. -->
      <div id="angular-scale-bar" class="hidden" role="img" aria-label="Horizontal angular scale, degrees from north, centred on the view direction"></div>
      <div id="osm-minimap"></div>
      <div id="hud-bottom">
        <div id="panel-route" class="card hidden">
          <div id="route-buttons">
            <button id="route-playpause">Play</button>
            <button id="route-slower" title="Slower (-)">&minus;</button>
            <button id="route-faster" title="Faster (+)">+</button>
            <button id="route-reset">Reset</button>
            <button id="route-loop" class="active">Loop</button>
            <button id="route-aim">Aim: Array centre</button>
            <button id="route-hint-toggle" type="button" aria-expanded="false" aria-controls="route-hint" title="Route controls help">?</button>
          </div>
          <input id="route-scrubber" type="range" min="0" max="1" step="1" value="0" />
          <div id="route-readout">km 0.0 / 0.0  |  distance 0 m  |  nearest turbine n/a</div>
          <div id="route-hint" class="hidden">Use the arrow keys to look around while you travel. Aim cycles Array centre &rarr; Nearest turbine &rarr; Road (looks along the road ahead); free-look snaps back on the next Aim press.</div>
        </div>
        <!-- Methodology caption (issue #178) and assessment-basis readout
             (issue #179): a two-line disclosure stack, always visible (unlike
             the old True-scale-only viewing-distance hint this replaces) —
             the wording itself carries whatever caveat the active preset or
             scenario needs, so there is nothing to show/hide. Each wraps
             (style.css: normal white-space, no ellipsis) rather than
             clipping — the safety-critical wording is put first and kept
             short by the formatter, so it always renders in full; see
             ui/methodologyCaption.ts / ui/assessmentBasis.ts. Populated by
             renderMethodologyCaption/renderAssessmentBasis in main.ts, never
             read by any LOS result (hard constraint #1). -->
        <div id="methodology-caption" class="card disclosure-card"></div>
        <div id="assessment-basis" class="card disclosure-card"></div>
        <div id="hud-strip" class="card">
          <div id="readout">&nbsp;</div>
          <div id="status">loading&hellip;</div>
        </div>
        <div id="caveat" class="card">
          <button id="caveat-toggle" type="button" aria-expanded="false" aria-controls="caveat-body">
            Illustrative visualisation &mdash; not a formal assessment <span aria-hidden="true">&#9432;</span>
          </button>
          <div id="caveat-body" class="hidden">
            Illustrative community visualisation of potential Ray II visibility, not a formal LVIA,
            photomontage, ZTV, or planning-certified visual assessment.
            Buildings, water and roads © OpenStreetMap contributors (ODbL).
          </div>
        </div>
        <div id="route-strip" class="card hidden"></div>
        <button id="mobile-sheet-toggle" type="button" aria-expanded="false" aria-controls="mobile-sheet">&#9776; More</button>
      </div>
      <div id="mobile-sheet" class="card hidden">
        <div id="mobile-sheet-header">
          <span id="mobile-sheet-title">More controls</span>
          <button id="mobile-sheet-close" type="button" aria-label="Close">&times;</button>
        </div>
        <div id="mobile-sheet-content">
          <div id="route-sheet-section"></div>
          <div id="map-sheet-section">
            <div class="sheet-section-heading">Map</div>
            <button id="map-recentre-btn" type="button">Recentre</button>
            <label class="layer-row"><input type="checkbox" id="map-labels-toggle" /><span>Labels</span></label>
          </div>
        </div>
      </div>
    </div>
  `;
}

void boot();
